/**
 * Minimal i18n for the Mini App.
 *
 * Language is picked from `Telegram.WebApp.initDataUnsafe.user.language_code`
 * (e.g. "pt-br", "es", "zh", "en"). Falls back to English.
 *
 * Usage:
 *   const t = useT();
 *   t('deposit.title')
 *   t('withdraw.exceeds_balance', { amount: '12.34' })
 */

import { useMemo, useSyncExternalStore } from 'react';
import { tryGetTelegram } from './telegram';

export type Lang = 'en' | 'pt-BR' | 'es' | 'zh';

export const SUPPORTED_LANGS: Lang[] = ['en', 'pt-BR', 'es', 'zh'];

const LANG_STORAGE_KEY = 'torian.lang';

const listeners = new Set<() => void>();
function emitLangChange() {
  for (const l of listeners) l();
}
function subscribeLang(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getStoredLang(): Lang | null {
  try {
    const v = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (v && (SUPPORTED_LANGS as string[]).includes(v)) return v as Lang;
  } catch {
    // localStorage may be unavailable (SSR, private mode)
  }
  return null;
}

export function setStoredLang(lang: Lang | null): void {
  try {
    if (lang === null) window.localStorage.removeItem(LANG_STORAGE_KEY);
    else window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // ignore
  }
  emitLangChange();
}

const DICT: Record<Lang, Record<string, string>> = {
  en: {
    'common.loading': 'Loading…',
    'common.back_to_chat': 'Back to chat',
    'common.copy_address': 'Copy address',
    'common.copy_full_address': 'Copy full address',
    'common.copied': '✓ Copied',
    'common.auth_error': 'Auth error: {error}',
    'common.error': 'Error: {message}',
    'common.balance': 'Balance',

    'home.title': 'Torian',
    'home.ready': 'Mini App ready.',
    'home.loading_title': 'Preparing your Mini App',
    'home.loading_desc': 'Authenticating your Telegram session and syncing your Polymarket wallet.',
    'home.loading_short': 'Authenticating your session.',
    'home.auth_eyebrow': 'Auth',
    'home.auth_failed': 'Authentication failed',
    'home.wallet': 'Wallet: {address}',
    'home.foreign.title': 'Funds in the wrong token',
    'home.foreign.body':
      'We detected tokens in your wallet that Polymarket does not accept for trading. Only <b>pUSD</b> (or <b>USDC</b>/<b>USDC.e</b> via the Bridge) shows up as balance.',
    'home.foreign.list_label': 'Detected:',
    'home.foreign.cta':
      'You can rescue these funds yourself, gasless — no support needed.',
    'home.foreign.cta_pusd': 'Convert to pUSD',
    'home.foreign.cta_eoa': 'Send to my wallet',
    'home.balance_title': 'pUSD balance',
    'home.balance_loading': 'Loading…',
    'home.deploy_wallet_cta': 'Deploy wallet (gasless)',
    'home.section_browse': 'Browse',
    'home.section_social': 'Social',
    'home.section_money': 'Money',
    'home.tile.markets_title': 'Markets',
    'home.tile.markets_desc': 'Browse Polymarket categories.',
    'home.tile.portfolio_title': 'Portfolio',
    'home.tile.portfolio_desc': 'Your open positions.',
    'home.tile.orders_title': 'Orders',
    'home.tile.orders_desc': 'Resting limit orders.',
    'home.tile.ranking_title': 'Ranking',
    'home.tile.ranking_desc': 'Top traders by volume.',
    'home.tile.copy_title': 'Copy trading',
    'home.tile.copy_desc': 'Follow leaders, get alerts.',
    'home.tile.deposit_title': 'Deposit',
    'home.tile.deposit_desc': 'Top up your pUSD balance.',
    'home.tile.withdraw_title': 'Withdraw',
    'home.tile.withdraw_desc': 'Move pUSD off Polymarket.',
    'home.tile.swap_title': 'Convert',
    'home.tile.swap_desc': 'Gasless USDC → pUSD.',
    'home.tile.bridge_title': 'Bridge',
    'home.tile.bridge_desc': 'Polymarket cross-chain deposits.',
    'home.tile.approvals_title': 'Enable trading',
    'home.tile.approvals_desc': 'One-time on-chain setup.',

    'rescue.preparing_title': 'Preparing rescue',
    'rescue.preparing_desc':
      'Authenticating your Telegram session before scanning your wallet.',
    'rescue.preparing_short': 'Authenticating…',
    'rescue.scanning_title': 'Scanning your wallet',
    'rescue.scanning_desc':
      'Reading on-chain balances and building the gasless rescue batch.',
    'rescue.scanning_short': 'Scanning…',
    'rescue.failed': 'Rescue failed',
    'rescue.detected_label': 'Detected',
    'rescue.detected_title': 'Tokens to rescue',
    'rescue.dest_label': 'Destination',
    'rescue.gas_label': 'Gas',
    'rescue.signing': 'Signing…',
    'rescue.submitting': 'Submitting…',
    'rescue.submitted_title': 'Rescue submitted',
    'rescue.submitted_badge': 'Submitted',
    'rescue.submitted_body': 'Your rescue is on Polymarket’s relayer',
    'rescue.submitted_desc':
      'Confirmation usually arrives within ~30 seconds. Balances will refresh automatically.',
    'rescue.state_label': 'State',
    'rescue.view_on_polygonscan': 'View on Polygonscan',
    'rescue.eoa.title': 'Send funds to my wallet',
    'rescue.eoa.desc':
      'Returns POL / WMATIC / USDT from your deposit wallet to your own EOA. Gasless via Polymarket’s relayer.',
    'rescue.eoa.cta': 'Sign & send to my wallet',
    'rescue.pusd.title': 'Convert to pUSD',
    'rescue.pusd.desc':
      'Wraps native POL and swaps everything to pUSD via Uniswap V3, in a single gasless batch. The result lands as tradeable pUSD in your deposit wallet.',
    'rescue.pusd.cta': 'Sign & convert to pUSD',
    'rescue.pusd.dest_value': 'pUSD on your deposit wallet',

    'deploy.title': 'Wallet setup',
    'deploy.creating': 'Creating your wallet…',
    'deploy.init_failed': 'Could not initialize wallet.',
    'deploy.ready_blurb':
      'Your wallet is ready. Polymarket uses a smart-wallet (proxy) on Polygon — the proxy is deployed automatically on your first deposit.',
    'deploy.your_address': 'Your address (EOA)',
    'deploy.backed_by': 'Backed by {backend}',
    'deploy.backend_web3auth': 'Web3Auth MPC',
    'deploy.backend_local': 'local key (dev mode)',
    'deploy.done': 'Done — back to chat',
    'deploy.close_hint': 'You can close this window using the ✕ at the top.',

    'deposit.title': 'Deposit',
    'deposit.blurb_bridge':
      'Send <b>USDC</b> or <b>USDC.e</b> on <b>Polygon</b> to the bridge address below. It will be converted to pUSD and credited to your wallet automatically. Minimum $2.',
    'deposit.blurb_direct':
      'Send <b>pUSD</b> on <b>Polygon</b> to the address below.',
    'deposit.bridge_label': 'Polymarket Bridge (Polygon)',
    'deposit.direct_label': 'Your deposit address',
    'deposit.not_deployed_title': '⚠ Wallet not deployed yet.',
    'deposit.not_deployed_body':
      'The contract is deployed on first use. You can deposit before deploying, but trading requires deployment.',
    'deposit.deploy_btn': 'Deploy now (gasless)',
    'deposit.deploying': 'Deploying…',
    'deposit.footer_bridge':
      'Polygon-only. Supports USDC, USDC.e, USDT, DAI, WETH, POL, SAND. Other chains via Polymarket Bridge.',
    'deposit.footer_direct':
      'Only send pUSD on Polygon. Sending other tokens directly here may cause permanent loss.',
    'deposit.intent_buy_title': 'Complete deposit to continue your trade',
    'deposit.intent_buy_body':
      'You wanted to buy <b>{outcome}</b> for <b>${usd}</b>. You need at least <b>${required}</b> in your wallet.',
    'deposit.intent_progress': 'Balance: <b>${balance}</b> / ${required}',
    'deposit.intent_short': 'Missing <b>${missing}</b>',
    'deposit.intent_ready_title': '✅ You’re ready to trade',
    'deposit.intent_ready_body':
      'Balance is sufficient. Go back to the chat and tap the same button to retry the buy.',
    'deposit.intent_back_to_chat': 'Back to chat',
    'deposit.intent_continue_button': 'Continue purchase',
    'deposit.intent_continuing': 'Preparing order…',
    'deposit.loading_title': 'Preparing deposit',
    'deposit.loading_desc':
      'Authenticating your Telegram session and resolving the bridge address.',
    'deposit.loading_short': 'Resolving the bridge address.',
    'deposit.auth_failed': 'Authentication failed',
    'deposit.load_failed': 'Could not load your wallet',
    'deposit.qr_alt': 'Deposit QR code',
    'deposit.qr_card_title': 'Scan or copy to send funds',
    'deposit.qr_card_desc':
      'QR encodes the destination address only — set the amount in your sending app.',
    'deposit.field_network': 'Network',
    'deposit.field_token': 'Token',
    'deposit.field_status': 'Status',
    'deposit.status_deployed': 'Deployed',
    'deposit.status_not_deployed': 'Not deployed',
    'deposit.action_required': 'Action required',

    'withdraw.title': 'Withdraw pUSD',
    'withdraw.title_sent': 'Withdraw sent',
    'withdraw.title_confirm': 'Confirm withdraw',
    'withdraw.authenticating': 'Authenticating…',
    'withdraw.loading_balance': 'Loading balance…',
    'withdraw.sent_blurb':
      'Sent <b>${amount}</b> pUSD to {to}. Funds usually arrive in a few seconds.',
    'withdraw.state': 'State',
    'withdraw.view_polygonscan': 'View on Polygonscan',
    'withdraw.available': 'Available',
    'withdraw.daily_limit': 'Daily limit: ${limit}',
    'withdraw.daily_limit_label': 'Daily limit',
    'withdraw.remaining_today': 'Remaining today: ${remaining}',
    'withdraw.destination_label': 'Destination (Polygon address)',
    'withdraw.destination_warning':
      'Polygon (MATIC) network only. Sending to a wrong network = lost funds.',
    'withdraw.amount_label': 'Amount (pUSD)',
    'withdraw.max': 'Max',
    'withdraw.exceeds_balance': 'Exceeds balance (${amount})',
    'withdraw.exceeds_daily': 'Exceeds daily remaining (${amount})',
    'withdraw.enter_positive': 'Enter a positive amount',
    'withdraw.preparing': 'Preparing…',
    'withdraw.review': 'Review',
    'withdraw.to': 'To',
    'withdraw.amount': 'Amount',
    'withdraw.network_fee': 'Network fee',
    'withdraw.gasless': 'Gasless (sponsored)',
    'withdraw.remaining_after': 'Remaining after',
    'withdraw.sign_send': 'Sign & send',
    'withdraw.signing': 'Signing…',
    'withdraw.submitting': 'Submitting…',
    'withdraw.back': 'Back',

    'swap.eyebrow': 'Swap',
    'swap.title': 'Convert USDC → pUSD',
    'swap.description':
      'Polymarket trades settle in pUSD. Convert the USDC sitting in your wallet — gasless via Polymarket’s relayer.',
    'swap.preparing_title': 'Preparing the swap',
    'swap.preparing_desc':
      'Authenticating your Telegram session before quoting USDC → pUSD.',
    'swap.preparing_short': 'Authenticating your session.',
    'swap.quoting_title': 'Quoting USDC → pUSD',
    'swap.quoting_desc':
      'Asking Polymarket’s relayer for an up-to-date conversion rate.',
    'swap.quoting_short': 'Quoting the swap.',
    'swap.step_session': 'Session',
    'swap.step_wallet': 'Wallet',
    'swap.step_relayer': 'Relayer',
    'swap.quote_label': 'Quote',
    'swap.from_label': 'From',
    'swap.to_min_label': 'To (min)',
    'swap.wallet_label': 'Wallet',
    'swap.gas_label': 'Gas',
    'swap.gasless': 'Gasless',
    'swap.usdc_on_polygon': 'USDC on Polygon',
    'swap.cta_sign': 'Sign & convert',
    'swap.signing': 'Signing…',
    'swap.submitting': 'Submitting…',
    'swap.failed_title': 'Swap failed',
    'swap.auth_failed_title': 'Authentication failed',
    'swap.no_balance_title': 'No USDC to convert',
    'swap.no_balance_body':
      'Your wallet has no USDC or USDC.e balance to swap. Deposit USDC first and try again.',
    'swap.cta_deposit': 'Deposit USDC',
    'swap.submitted_eyebrow': 'Submitted',
    'swap.submitted_title': 'Swap submitted',
    'swap.submitted_body': 'Your conversion is on Polymarket’s relayer',
    'swap.submitted_desc':
      'Confirmation usually arrives within ~30 seconds. Your pUSD balance will refresh automatically.',
    'swap.state_label': 'State',
    'swap.view_polygonscan': 'View on Polygonscan',

    'market.eyebrow': 'Markets',
    'market.title': 'Browse markets',
    'market.description':
      'Pick a category to see live Polymarket questions, or search by name.',
    'market.breadcrumb': 'Markets',
    'market.search_label': 'Search',
    'market.search_placeholder': 'e.g. Trump, World Cup, Bitcoin…',
    'market.search_cta': 'Search',
    'market.categories_title': 'Categories',
    'market.categories_description':
      'Sixteen curated topics, refreshed live from Polymarket.',
    'market.cat.breaking': 'Breaking',
    'market.cat.politics': 'Politics',
    'market.cat.trump': 'Trump',
    'market.cat.crypto': 'Crypto',
    'market.cat.sports': 'Sports',
    'market.cat.elections': 'Elections',
    'market.cat.world': 'World',
    'market.cat.business': 'Business',
    'market.cat.geopolitics': 'Geopolitics',
    'market.cat.finance': 'Finance',
    'market.cat.tech': 'Tech',
    'market.cat.culture': 'Culture',
    'market.cat.economy': 'Economy',
    'market.cat.mentions': 'Mentions',
    'market.cat.climate': 'Climate',
    'market.cat.bonds': 'Bonds',

    'market.list.eyebrow_cat': '{category}',
    'market.list.eyebrow_search': 'Search',
    'market.list.title_cat': '{category}',
    'market.list.title_search': '“{term}”',
    'market.list.description_cat':
      'Live markets in this category, refreshed from Polymarket.',
    'market.list.description_search':
      'Markets matching your search across all categories.',
    'market.list.breadcrumb_cat': '{category}',
    'market.list.breadcrumb_search': 'Search: {term}',
    'market.list.loading_title': 'Loading markets…',
    'market.list.loading_desc':
      'Fetching the latest questions and prices from Polymarket.',
    'market.list.loading_short': 'Loading markets.',
    'market.list.error_title': 'Could not load markets',
    'market.list.empty_title': 'No markets found',
    'market.list.empty_cat':
      'There are no live markets in this category right now. Try another category or check back later.',
    'market.list.empty_search':
      'No markets match “{term}”. Try a different keyword or browse by category.',
    'market.list.cta_categories': 'Browse categories',
    'market.list.volume24h': '24h vol',
    'market.list.no_volume': '—',
    'market.list.ends': 'Ends {date}',
    'market.list.prev': 'Previous',
    'market.list.next': 'Next',
    'market.list.page_of': 'Page {page} of {total}',

    'market.detail.eyebrow': 'Market',
    'market.detail.breadcrumb': 'Market',
    'market.detail.loading_title': 'Loading market…',
    'market.detail.loading_desc':
      'Fetching prices, outcomes and order-book preview from Polymarket.',
    'market.detail.loading_short': 'Loading market.',
    'market.detail.error_title': 'Could not load market',
    'market.detail.outcomes_title': 'Outcomes',
    'market.detail.outcomes_description':
      'Tap an amount to draft a BUY order. You’ll review and sign on the next screen.',
    'market.detail.preview_title': 'Order-book preview',
    'market.detail.preview_description':
      'Best bid / ask snapshot from the CLOB. Real fills depend on live liquidity.',
    'market.detail.bestbid_label': 'Best bid',
    'market.detail.bestask_label': 'Best ask',
    'market.detail.spread_label': 'Spread',
    'market.detail.volume24h_label': '24h volume',
    'market.detail.volume_total_label': 'Total volume',
    'market.detail.liquidity_label': 'Liquidity',
    'market.detail.ends_label': 'Ends',
    'market.detail.about_title': 'About this market',
    'market.detail.buy_for': 'Buy {outcome} · ${amount}',
    'market.detail.drafting': 'Drafting…',
    'market.detail.min_order': 'Min order: ${amount}',
    'market.detail.auth_required':
      'Sign in to your Telegram session to place orders.',
    'market.detail.draft_failed_title': 'Could not draft order',
    'market.detail.no_balance_title': 'Not enough pUSD',
    'market.detail.no_balance_body': 'You have ${have}, this order needs ${need}. Top up your wallet and try again.',
    'market.detail.deposit_cta': 'Deposit pUSD',
    'market.detail.approvals_title': 'Enable trading first',
    'market.detail.approvals_body':
      'Before placing your first order you need a one-time setup that authorizes Polymarket to move funds from your deposit wallet. It is gasless and only needs to be done once.',
    'market.detail.approvals_cta': 'Enable trading',

    'portfolio.eyebrow': 'Portfolio',
    'portfolio.breadcrumb': 'Portfolio',
    'portfolio.title': 'Your portfolio',
    'portfolio.description':
      'Open positions on Polymarket. Each share pays $1 if your side wins.',
    'portfolio.loading_title': 'Loading your portfolio…',
    'portfolio.loading_desc':
      'Fetching open positions and current values from Polymarket.',
    'portfolio.loading_short': 'Loading positions.',
    'portfolio.error_title': 'Could not load portfolio',
    'portfolio.empty_title': 'You don’t have positions yet',
    'portfolio.empty_intro': 'Make your first prediction in 3 steps:',
    'portfolio.empty_step_1':
      '1. Deposit pUSD — fund your wallet (USDC, USDT or pUSD).',
    'portfolio.empty_step_2':
      '2. Browse markets — pick a question that interests you.',
    'portfolio.empty_step_3':
      '3. Buy YES or NO — each share pays $1 if your side wins.',
    'portfolio.empty_cta_deposit': 'Deposit pUSD',
    'portfolio.empty_cta_markets': 'Browse markets',
    'portfolio.empty_cta_help': 'How it works',
    'portfolio.totals_title': 'Totals',
    'portfolio.payout_label': 'Max payout',
    'portfolio.value_label': 'Current value',
    'portfolio.size_label': 'Size',
    'portfolio.avg_label': 'Avg',
    'portfolio.cur_label': 'Now',
    'portfolio.pnl_label': 'PnL',
    'portfolio.resolves_label': 'Resolves',
    'portfolio.redeemable_badge': 'Redeemable',
    'portfolio.cta_open_market': 'Open market',

    'orders.eyebrow': 'Orders',
    'orders.breadcrumb': 'Orders',
    'orders.title': 'Open orders',
    'orders.description':
      'Limit orders resting in the Polymarket book. Tap a card to see the market or cancel.',
    'orders.loading_title': 'Loading your orders…',
    'orders.loading_desc': 'Fetching open orders from Polymarket CLOB.',
    'orders.loading_short': 'Loading orders.',
    'orders.error_title': 'Could not load orders',
    'orders.empty_title': 'No {status} orders',
    'orders.empty_intro':
      'Place a limit order from any market to reserve a price. Open orders show up here until they fill or you cancel.',
    'orders.empty_cta_markets': 'Browse markets',
    'orders.filter_all': 'All',
    'orders.filter_open': 'Open',
    'orders.filter_pending': 'Pending',
    'orders.filter_matched': 'Filled',
    'orders.filter_cancelled': 'Cancelled',
    'orders.filter_failed': 'Failed',
    'orders.status_pending': 'Pending',
    'orders.status_open': 'Open',
    'orders.status_matched': 'Filled',
    'orders.status_cancelled': 'Cancelled',
    'orders.status_failed': 'Failed',
    'orders.side_buy': 'BUY',
    'orders.side_sell': 'SELL',
    'orders.size_label': 'Size',
    'orders.price_label': 'Price',
    'orders.total_label': 'Total',
    'orders.created_label': 'Placed',
    'orders.type_label': 'Type',
    'orders.cancel_btn': 'Cancel order',
    'orders.cancelling': 'Cancelling…',
    'orders.cancel_confirm': 'Cancel this order? This cannot be undone.',
    'orders.cancel_failed_title': 'Could not cancel order',
    'orders.view_market': 'View market',

    'ranking.eyebrow': 'Ranking',
    'ranking.title': 'Top traders',
    'ranking.description': 'Top users by trade volume.',
    'ranking.breadcrumb': 'Ranking',
    'ranking.loading_title': 'Loading ranking',
    'ranking.loading_desc': 'Aggregating volume across the selected period.',
    'ranking.loading_short': 'Loading ranking…',
    'ranking.empty_title': 'No traders yet',
    'ranking.empty_desc': 'No trades have been recorded for this period.',
    'ranking.error_title': 'Could not load ranking',
    'ranking.period_day': '24h',
    'ranking.period_week': 'Week',
    'ranking.period_month': 'Month',
    'ranking.period_all': 'All-time',
    'ranking.volume_label': 'Volume',
    'ranking.trades_label': 'Trades',
    'ranking.you_badge': 'You',
    'ranking.anonymous': 'Anonymous',

    'copy.eyebrow': 'Copy trading',
    'copy.title': 'Following',
    'copy.description': 'Get notified when these leaders trade. Auto-execution coming soon.',
    'copy.breadcrumb': 'Copy',
    'copy.loading_title': 'Loading follows',
    'copy.loading_desc': 'Fetching the leaders you follow.',
    'copy.loading_short': 'Loading…',
    'copy.empty_title': 'You’re not following anyone',
    'copy.empty_desc': 'Add a leader address to get notifications when they place trades.',
    'copy.add_title': 'Follow a leader',
    'copy.add_address_label': 'Leader address',
    'copy.add_address_placeholder': '0x…',
    'copy.add_max_label': 'Max USD per trade (optional)',
    'copy.add_mult_label': 'Size multiplier (optional)',
    'copy.add_btn': 'Follow leader',
    'copy.adding': 'Following…',
    'copy.add_error_title': 'Could not follow',
    'copy.invalid_address': 'Enter a valid 0x address (40 hex chars).',
    'copy.unfollow_btn': 'Unfollow',
    'copy.unfollow_confirm': 'Unfollow this leader?',
    'copy.unfollowing': 'Removing…',
    'copy.unfollow_error_title': 'Could not unfollow',
    'copy.row_max': 'Max ${max}',
    'copy.row_mult': '{mult}×',
    'copy.row_since': 'Following since {date}',
    'copy.active': 'Active',
    'copy.inactive': 'Inactive',
    'copy.notice_title': 'Notification-only',
    'copy.notice_desc': 'You get alerted when a followed leader trades. Auto-execution arrives with session keys.',
    'copy.top_title': 'Top traders',
    'copy.top_loading': 'Loading top traders…',
    'copy.top_empty': 'No traders yet. Check back soon.',
    'copy.top_volume': '{vol} vol',
    'copy.top_pnl': '{pnl} PnL',
    'copy.leader.breadcrumb': 'Trader',
    'copy.leader.eyebrow': 'Trader',
    'copy.leader.loading_title': 'Loading trader',
    'copy.leader.loading_desc': 'Fetching this trader’s recent activity.',
    'copy.leader.loading_short': 'Loading…',
    'copy.leader.title': 'Trader profile',
    'copy.leader.load_failed': 'Could not load trader',
    'copy.leader.not_found': 'This trader was not found.',
    'copy.leader.how_title': 'How copy works',
    'copy.leader.how_desc': 'We’ll notify you on Telegram with this trader’s latest moves. Each alert has a button to open the app and copy the order in one tap.',
    'copy.leader.follow_title': 'Follow & get alerts',
    'copy.leader.follow_btn': 'Follow trader',
    'copy.leader.recent_title': 'Recent trades',
    'copy.leader.recent_empty': 'No recent trades.',
    'copy.leader.just_now': 'just now',
    'copy.leader.mins_ago': '{n}m ago',
    'copy.leader.hours_ago': '{n}h ago',
    'copy.leader.days_ago': '{n}d ago',
    'copy.confirm.breadcrumb': 'Copy',
    'copy.confirm.eyebrow': 'One-click copy',
    'copy.confirm.loading_title': 'Loading signal',
    'copy.confirm.loading_desc': 'Fetching the trade to copy.',
    'copy.confirm.loading_short': 'Loading…',
    'copy.confirm.title': 'Copy this trade',
    'copy.confirm.description': 'Replicate {leader}’s move.',
    'copy.confirm.load_failed': 'Could not load signal',
    'copy.confirm.not_found': 'This signal was not found or expired.',
    'copy.confirm.side_buy': 'Buy',
    'copy.confirm.side_sell': 'Sell',
    'copy.confirm.field_outcome': 'Outcome',
    'copy.confirm.field_price': 'Price',
    'copy.confirm.field_leader': 'Trader',
    'copy.confirm.field_status': 'Status',
    'copy.confirm.amount_title': 'Amount',
    'copy.confirm.amount_label': 'USD to spend',
    'copy.confirm.action_error': 'Action failed',
    'copy.confirm.handled_title': 'Already handled',
    'copy.confirm.handled_desc': 'This signal was already executed or dismissed.',
    'copy.confirm.confirming': 'Preparing…',
    'copy.confirm.confirm_btn': 'Copy now',
    'copy.confirm.dismiss_btn': 'Dismiss',

    'help.eyebrow': 'Help',
    'help.title': 'How to use Torian',
    'help.description': 'Quick reference for the Mini App and bot commands.',
    'help.breadcrumb': 'Help',
    'help.section_app': 'Mini App',
    'help.section_bot': 'Bot commands',
    'help.section_links': 'Resources',
    'help.app.markets_label': 'Markets',
    'help.app.markets_desc': 'Browse Polymarket categories and search markets.',
    'help.app.portfolio_label': 'Portfolio',
    'help.app.portfolio_desc': 'Your open positions and total value.',
    'help.app.orders_label': 'Orders',
    'help.app.orders_desc': 'Your active and historical limit orders.',
    'help.app.deposit_label': 'Deposit / Bridge',
    'help.app.deposit_desc': 'Top up pUSD via Polygon USDC or the Polymarket Bridge.',
    'help.app.withdraw_label': 'Withdraw',
    'help.app.withdraw_desc': 'Move pUSD back to your wallet on Polygon.',
    'help.app.ranking_label': 'Ranking',
    'help.app.ranking_desc': 'Top traders by trade volume.',
    'help.app.copy_label': 'Copy trading',
    'help.app.copy_desc': 'Follow leaders and get notified on their trades.',
    'help.bot.market': '`/market <term>` — search markets',
    'help.bot.portfolio': '`/portfolio` — your portfolio',
    'help.bot.wallet': '`/wallet` — your wallet',
    'help.bot.deposit': '`/deposit` — deposit funds',
    'help.bot.withdraw': '`/withdraw` — withdraw',
    'help.bot.whale': '`/whale` — recent whale trades',
    'help.bot.copy': '`/copy` — copy a leader (notify only)',
    'help.bot.ranking': '`/ranking` — top traders',
    'help.bot.lang': '`/lang` — change language',
    'help.bot.help': '`/help` — show this message',
    'help.links.polymarket': 'Open Polymarket',
    'help.links.support': 'Contact support',

    'settings.eyebrow': 'Settings',
    'settings.title': 'Settings',
    'settings.description': 'Manage your Mini App preferences.',
    'settings.breadcrumb': 'Settings',
    'deposit.breadcrumb': 'Deposit',
    'withdraw.breadcrumb': 'Withdraw',
    'swap.breadcrumb': 'Swap',
    'deploy.breadcrumb': 'Deploy',
    'rescue.breadcrumb': 'Rescue',
    'approvals.breadcrumb': 'Approvals',
    'bridge.breadcrumb': 'Bridge',
    'sign_order.breadcrumb': 'Sign order',
    'settings.lang_title': 'Language',
    'settings.lang_desc': 'Used across the Mini App. The bot keeps its own setting.',
    'settings.lang_auto': 'Auto (from Telegram)',
    'settings.lang_saved': 'Language updated.',
    'settings.address_title': 'Wallet',
    'settings.address_desc': 'Your deposit address on Polygon.',
    'settings.user_id_label': 'User ID',
    'settings.session_title': 'Session',
    'settings.signed_in_as': 'Signed in as',
    'settings.about_title': 'About',
    'settings.version_label': 'Version',
    'settings.reset_lang_btn': 'Reset language',
  },
  'pt-BR': {
    'common.loading': 'Carregando…',
    'common.back_to_chat': 'Voltar ao chat',
    'common.copy_address': 'Copiar endereço',
    'common.copy_full_address': 'Copiar endereço completo',
    'common.copied': '✓ Copiado',
    'common.auth_error': 'Erro de autenticação: {error}',
    'common.error': 'Erro: {message}',
    'common.balance': 'Saldo',

    'home.title': 'Torian',
    'home.ready': 'Mini App pronta.',
    'home.loading_title': 'Preparando sua Mini App',
    'home.loading_desc': 'Autenticando sua sessão do Telegram e sincronizando sua carteira Polymarket.',
    'home.loading_short': 'Autenticando sua sessão.',
    'home.auth_eyebrow': 'Auth',
    'home.auth_failed': 'Falha na autenticação',
    'home.wallet': 'Carteira: {address}',
    'home.foreign.title': 'Fundos no token errado',
    'home.foreign.body':
      'Detectamos tokens na sua carteira que a Polymarket não aceita pra trade. Só <b>pUSD</b> (ou <b>USDC</b>/<b>USDC.e</b> via Bridge) aparece como saldo.',
    'home.foreign.list_label': 'Detectado:',
    'home.foreign.cta':
      'Você mesmo pode resgatar esses fundos, sem gas — sem precisar de suporte.',
    'home.foreign.cta_pusd': 'Converter pra pUSD',
    'home.foreign.cta_eoa': 'Mandar pra minha carteira',
    'home.balance_title': 'Saldo pUSD',
    'home.balance_loading': 'Carregando…',
    'home.deploy_wallet_cta': 'Implantar carteira (sem gas)',
    'home.section_browse': 'Explorar',
    'home.section_social': 'Social',
    'home.section_money': 'Dinheiro',
    'home.tile.markets_title': 'Mercados',
    'home.tile.markets_desc': 'Categorias da Polymarket.',
    'home.tile.portfolio_title': 'Portfólio',
    'home.tile.portfolio_desc': 'Suas posições abertas.',
    'home.tile.orders_title': 'Ordens',
    'home.tile.orders_desc': 'Ordens limite no livro.',
    'home.tile.ranking_title': 'Ranking',
    'home.tile.ranking_desc': 'Top traders por volume.',
    'home.tile.copy_title': 'Copy trading',
    'home.tile.copy_desc': 'Siga líderes, receba avisos.',
    'home.tile.deposit_title': 'Depositar',
    'home.tile.deposit_desc': 'Adicionar pUSD ao saldo.',
    'home.tile.withdraw_title': 'Sacar',
    'home.tile.withdraw_desc': 'Tirar pUSD da Polymarket.',
    'home.tile.swap_title': 'Converter',
    'home.tile.swap_desc': 'USDC → pUSD sem gas.',
    'home.tile.bridge_title': 'Bridge',
    'home.tile.bridge_desc': 'Depósitos entre redes.',
    'home.tile.approvals_title': 'Liberar trade',
    'home.tile.approvals_desc': 'Setup on-chain (1x).',

    'rescue.preparing_title': 'Preparando resgate',
    'rescue.preparing_desc':
      'Autenticando sua sessão do Telegram antes de escanear a carteira.',
    'rescue.preparing_short': 'Autenticando…',
    'rescue.scanning_title': 'Escaneando sua carteira',
    'rescue.scanning_desc':
      'Lendo saldos on-chain e montando o batch de resgate sem gas.',
    'rescue.scanning_short': 'Escaneando…',
    'rescue.failed': 'Resgate falhou',
    'rescue.detected_label': 'Detectado',
    'rescue.detected_title': 'Tokens a resgatar',
    'rescue.dest_label': 'Destino',
    'rescue.gas_label': 'Gas',
    'rescue.signing': 'Assinando…',
    'rescue.submitting': 'Enviando…',
    'rescue.submitted_title': 'Resgate enviado',
    'rescue.submitted_badge': 'Enviado',
    'rescue.submitted_body': 'Seu resgate está no relayer da Polymarket',
    'rescue.submitted_desc':
      'A confirmação costuma chegar em ~30 segundos. Os saldos atualizam sozinhos.',
    'rescue.state_label': 'Estado',
    'rescue.view_on_polygonscan': 'Ver no Polygonscan',
    'rescue.eoa.title': 'Mandar fundos pra minha carteira',
    'rescue.eoa.desc':
      'Devolve POL / WMATIC / USDT da deposit wallet pra sua própria EOA. Sem gas, via relayer da Polymarket.',
    'rescue.eoa.cta': 'Assinar e enviar pra carteira',
    'rescue.pusd.title': 'Converter pra pUSD',
    'rescue.pusd.desc':
      'Faz wrap do POL nativo e converte tudo em pUSD via Uniswap V3, num único batch sem gas. O resultado já cai como pUSD tradável na deposit wallet.',
    'rescue.pusd.cta': 'Assinar e converter pra pUSD',
    'rescue.pusd.dest_value': 'pUSD na sua deposit wallet',

    'deploy.title': 'Configuração da carteira',
    'deploy.creating': 'Criando sua carteira…',
    'deploy.init_failed': 'Não foi possível inicializar a carteira.',
    'deploy.ready_blurb':
      'Sua carteira está pronta. A Polymarket usa uma smart-wallet (proxy) na Polygon — o proxy é implantado automaticamente no seu primeiro depósito.',
    'deploy.your_address': 'Seu endereço (EOA)',
    'deploy.backed_by': 'Protegida por {backend}',
    'deploy.backend_web3auth': 'Web3Auth MPC',
    'deploy.backend_local': 'chave local (modo dev)',
    'deploy.done': 'Pronto — voltar ao chat',
    'deploy.close_hint':
      'Você pode fechar esta janela usando o ✕ no topo.',

    'deposit.title': 'Depositar',
    'deposit.blurb_bridge':
      'Envie <b>USDC</b> ou <b>USDC.e</b> na rede <b>Polygon</b> para o endereço de ponte abaixo. O valor será convertido em pUSD e creditado automaticamente na sua carteira. Mínimo $2.',
    'deposit.blurb_direct':
      'Envie <b>pUSD</b> na rede <b>Polygon</b> para o endereço abaixo.',
    'deposit.bridge_label': 'Polymarket Bridge (Polygon)',
    'deposit.direct_label': 'Seu endereço de depósito',
    'deposit.not_deployed_title': '⚠ Carteira ainda não implantada.',
    'deposit.not_deployed_body':
      'O contrato é implantado no primeiro uso. Você pode depositar antes da implantação, mas para operar é preciso implantar.',
    'deposit.deploy_btn': 'Implantar agora (sem gas)',
    'deposit.deploying': 'Implantando…',
    'deposit.footer_bridge':
      'Apenas Polygon. Suporta USDC, USDC.e, USDT, DAI, WETH, POL, SAND. Outras redes via Polymarket Bridge.',
    'deposit.footer_direct':
      'Envie apenas pUSD na Polygon. Enviar outros tokens aqui pode causar perda permanente.',
    'deposit.intent_buy_title': 'Complete o depósito para continuar sua compra',
    'deposit.intent_buy_body':
      'Você quis comprar <b>{outcome}</b> por <b>${usd}</b>. Você precisa de pelo menos <b>${required}</b> na carteira.',
    'deposit.intent_progress': 'Saldo: <b>${balance}</b> / ${required}',
    'deposit.intent_short': 'Faltam <b>${missing}</b>',
    'deposit.intent_ready_title': '✅ Tudo pronto pra negociar',
    'deposit.intent_ready_body':
      'Saldo suficiente. Volte ao chat e toque no mesmo botão pra refazer a compra.',
    'deposit.intent_back_to_chat': 'Voltar ao chat',
    'deposit.intent_continue_button': 'Continuar compra',
    'deposit.intent_continuing': 'Preparando ordem…',
    'deposit.loading_title': 'Preparando depósito',
    'deposit.loading_desc':
      'Autenticando sua sessão do Telegram e resolvendo o endereço da bridge.',
    'deposit.loading_short': 'Resolvendo o endereço da bridge.',
    'deposit.auth_failed': 'Falha na autenticação',
    'deposit.load_failed': 'Não foi possível carregar sua carteira',
    'deposit.qr_alt': 'QR code do depósito',
    'deposit.qr_card_title': 'Escaneie ou copie para enviar fundos',
    'deposit.qr_card_desc':
      'O QR codifica apenas o endereço de destino — defina o valor no app de envio.',
    'deposit.field_network': 'Rede',
    'deposit.field_token': 'Token',
    'deposit.field_status': 'Status',
    'deposit.status_deployed': 'Implantado',
    'deposit.status_not_deployed': 'Não implantado',
    'deposit.action_required': 'Ação necessária',

    'withdraw.title': 'Sacar pUSD',
    'withdraw.title_sent': 'Saque enviado',
    'withdraw.title_confirm': 'Confirmar saque',
    'withdraw.authenticating': 'Autenticando…',
    'withdraw.loading_balance': 'Carregando saldo…',
    'withdraw.sent_blurb':
      'Enviado <b>${amount}</b> pUSD para {to}. Normalmente chega em segundos.',
    'withdraw.state': 'Estado',
    'withdraw.view_polygonscan': 'Ver no Polygonscan',
    'withdraw.available': 'Disponível',
    'withdraw.daily_limit': 'Limite diário: ${limit}',
    'withdraw.daily_limit_label': 'Limite diário',
    'withdraw.remaining_today': 'Restante hoje: ${remaining}',
    'withdraw.destination_label': 'Destino (endereço Polygon)',
    'withdraw.destination_warning':
      'Apenas rede Polygon (MATIC). Enviar para rede errada = perda dos fundos.',
    'withdraw.amount_label': 'Valor (pUSD)',
    'withdraw.max': 'Máx',
    'withdraw.exceeds_balance': 'Excede saldo (${amount})',
    'withdraw.exceeds_daily': 'Excede limite diário restante (${amount})',
    'withdraw.enter_positive': 'Informe um valor positivo',
    'withdraw.preparing': 'Preparando…',
    'withdraw.review': 'Revisar',
    'withdraw.to': 'Para',
    'withdraw.amount': 'Valor',
    'withdraw.network_fee': 'Taxa de rede',
    'withdraw.gasless': 'Sem gas (patrocinada)',
    'withdraw.remaining_after': 'Restante após',
    'withdraw.sign_send': 'Assinar e enviar',
    'withdraw.signing': 'Assinando…',
    'withdraw.submitting': 'Enviando…',
    'withdraw.back': 'Voltar',

    'swap.eyebrow': 'Swap',
    'swap.title': 'Converter USDC → pUSD',
    'swap.description':
      'Os trades da Polymarket liquidam em pUSD. Converta o USDC da sua carteira — sem gas, via relayer da Polymarket.',
    'swap.preparing_title': 'Preparando o swap',
    'swap.preparing_desc':
      'Autenticando sua sessão do Telegram antes de cotar USDC → pUSD.',
    'swap.preparing_short': 'Autenticando sessão.',
    'swap.quoting_title': 'Cotando USDC → pUSD',
    'swap.quoting_desc':
      'Pedindo ao relayer da Polymarket uma taxa de conversão atualizada.',
    'swap.quoting_short': 'Cotando o swap.',
    'swap.step_session': 'Sessão',
    'swap.step_wallet': 'Carteira',
    'swap.step_relayer': 'Relayer',
    'swap.quote_label': 'Cotação',
    'swap.from_label': 'De',
    'swap.to_min_label': 'Para (mín)',
    'swap.wallet_label': 'Carteira',
    'swap.gas_label': 'Gas',
    'swap.gasless': 'Sem gas',
    'swap.usdc_on_polygon': 'USDC na Polygon',
    'swap.cta_sign': 'Assinar e converter',
    'swap.signing': 'Assinando…',
    'swap.submitting': 'Enviando…',
    'swap.failed_title': 'Swap falhou',
    'swap.auth_failed_title': 'Falha de autenticação',
    'swap.no_balance_title': 'Sem USDC pra converter',
    'swap.no_balance_body':
      'Sua carteira não tem saldo de USDC nem USDC.e pra fazer o swap. Deposite USDC primeiro e tente de novo.',
    'swap.cta_deposit': 'Depositar USDC',
    'swap.submitted_eyebrow': 'Enviado',
    'swap.submitted_title': 'Swap enviado',
    'swap.submitted_body': 'Sua conversão está no relayer da Polymarket',
    'swap.submitted_desc':
      'A confirmação costuma chegar em ~30 segundos. Seu saldo de pUSD atualiza sozinho.',
    'swap.state_label': 'Estado',
    'swap.view_polygonscan': 'Ver no Polygonscan',

    'market.eyebrow': 'Mercados',
    'market.title': 'Explorar mercados',
    'market.description':
      'Escolha uma categoria pra ver perguntas ao vivo da Polymarket, ou busque pelo nome.',
    'market.breadcrumb': 'Mercados',
    'market.search_label': 'Buscar',
    'market.search_placeholder': 'ex.: Trump, Copa do Mundo, Bitcoin…',
    'market.search_cta': 'Buscar',
    'market.categories_title': 'Categorias',
    'market.categories_description':
      'Dezesseis temas curados, atualizados ao vivo da Polymarket.',
    'market.cat.breaking': 'Últimas',
    'market.cat.politics': 'Política',
    'market.cat.trump': 'Trump',
    'market.cat.crypto': 'Cripto',
    'market.cat.sports': 'Esportes',
    'market.cat.elections': 'Eleições',
    'market.cat.world': 'Mundo',
    'market.cat.business': 'Negócios',
    'market.cat.geopolitics': 'Geopolítica',
    'market.cat.finance': 'Finanças',
    'market.cat.tech': 'Tecnologia',
    'market.cat.culture': 'Cultura',
    'market.cat.economy': 'Economia',
    'market.cat.mentions': 'Menções',
    'market.cat.climate': 'Clima',
    'market.cat.bonds': 'Títulos',

    'market.list.eyebrow_cat': '{category}',
    'market.list.eyebrow_search': 'Busca',
    'market.list.title_cat': '{category}',
    'market.list.title_search': '“{term}”',
    'market.list.description_cat':
      'Mercados ao vivo desta categoria, atualizados da Polymarket.',
    'market.list.description_search':
      'Mercados que correspondem à sua busca em todas as categorias.',
    'market.list.breadcrumb_cat': '{category}',
    'market.list.breadcrumb_search': 'Busca: {term}',
    'market.list.loading_title': 'Carregando mercados…',
    'market.list.loading_desc':
      'Buscando as perguntas e preços mais recentes na Polymarket.',
    'market.list.loading_short': 'Carregando mercados.',
    'market.list.error_title': 'Não foi possível carregar os mercados',
    'market.list.empty_title': 'Nenhum mercado encontrado',
    'market.list.empty_cat':
      'Não há mercados ativos nesta categoria agora. Tente outra categoria ou volte mais tarde.',
    'market.list.empty_search':
      'Nenhum mercado corresponde a “{term}”. Tente outra palavra-chave ou navegue por categoria.',
    'market.list.cta_categories': 'Ver categorias',
    'market.list.volume24h': 'vol 24h',
    'market.list.no_volume': '—',
    'market.list.ends': 'Encerra {date}',
    'market.list.prev': 'Anterior',
    'market.list.next': 'Próximo',
    'market.list.page_of': 'Página {page} de {total}',

    'market.detail.eyebrow': 'Mercado',
    'market.detail.breadcrumb': 'Mercado',
    'market.detail.loading_title': 'Carregando mercado…',
    'market.detail.loading_desc':
      'Buscando preços, resultados e prévia do livro de ofertas na Polymarket.',
    'market.detail.loading_short': 'Carregando mercado.',
    'market.detail.error_title': 'Não foi possível carregar o mercado',
    'market.detail.outcomes_title': 'Resultados',
    'market.detail.outcomes_description':
      'Toque num valor pra criar uma ordem de COMPRA. Você revisa e assina na próxima tela.',
    'market.detail.preview_title': 'Prévia do livro',
    'market.detail.preview_description':
      'Melhor compra / venda do CLOB. Execução real depende da liquidez ao vivo.',
    'market.detail.bestbid_label': 'Melhor compra',
    'market.detail.bestask_label': 'Melhor venda',
    'market.detail.spread_label': 'Spread',
    'market.detail.volume24h_label': 'Volume 24h',
    'market.detail.volume_total_label': 'Volume total',
    'market.detail.liquidity_label': 'Liquidez',
    'market.detail.ends_label': 'Encerra',
    'market.detail.about_title': 'Sobre este mercado',
    'market.detail.buy_for': 'Comprar {outcome} · ${amount}',
    'market.detail.drafting': 'Criando…',
    'market.detail.min_order': 'Mínimo: ${amount}',
    'market.detail.auth_required':
      'Autentique sua sessão do Telegram pra abrir ordens.',
    'market.detail.draft_failed_title': 'Não foi possível criar a ordem',
    'market.detail.no_balance_title': 'Saldo pUSD insuficiente',
    'market.detail.no_balance_body': 'Você tem ${have} e essa ordem precisa de ${need}. Adicione fundos e tente de novo.',
    'market.detail.deposit_cta': 'Depositar pUSD',
    'market.detail.approvals_title': 'Primeiro libere o trade',
    'market.detail.approvals_body':
      'Antes da sua primeira ordem você precisa de uma liberação única que autoriza a Polymarket a movimentar os fundos da sua deposit wallet. É sem gas e só precisa ser feita uma vez.',
    'market.detail.approvals_cta': 'Liberar trade',

    'portfolio.eyebrow': 'Portfólio',
    'portfolio.breadcrumb': 'Portfólio',
    'portfolio.title': 'Seu portfólio',
    'portfolio.description':
      'Posições abertas na Polymarket. Cada cota paga $1 se seu lado vencer.',
    'portfolio.loading_title': 'Carregando seu portfólio…',
    'portfolio.loading_desc':
      'Buscando posições abertas e valores atuais na Polymarket.',
    'portfolio.loading_short': 'Carregando posições.',
    'portfolio.error_title': 'Não foi possível carregar o portfólio',
    'portfolio.empty_title': 'Você ainda não tem posições',
    'portfolio.empty_intro': 'Faça sua primeira previsão em 3 passos:',
    'portfolio.empty_step_1':
      '1. Depositar pUSD — financie sua carteira (USDC, USDT ou pUSD).',
    'portfolio.empty_step_2':
      '2. Explorar mercados — escolha uma pergunta que te interesse.',
    'portfolio.empty_step_3':
      '3. Comprar YES ou NO — cada cota paga $1 se seu lado vencer.',
    'portfolio.empty_cta_deposit': 'Depositar pUSD',
    'portfolio.empty_cta_markets': 'Explorar mercados',
    'portfolio.empty_cta_help': 'Como funciona',
    'portfolio.totals_title': 'Totais',
    'portfolio.payout_label': 'Payout máximo',
    'portfolio.value_label': 'Valor atual',
    'portfolio.size_label': 'Cotas',
    'portfolio.avg_label': 'Méd.',
    'portfolio.cur_label': 'Agora',
    'portfolio.pnl_label': 'PnL',
    'portfolio.resolves_label': 'Resolve',
    'portfolio.redeemable_badge': 'Resgatável',
    'portfolio.cta_open_market': 'Ver mercado',

    'orders.eyebrow': 'Ordens',
    'orders.breadcrumb': 'Ordens',
    'orders.title': 'Ordens abertas',
    'orders.description':
      'Ordens limite no livro da Polymarket. Toque num card pra ver o mercado ou cancelar.',
    'orders.loading_title': 'Carregando suas ordens…',
    'orders.loading_desc': 'Buscando ordens abertas no CLOB da Polymarket.',
    'orders.loading_short': 'Carregando ordens.',
    'orders.error_title': 'Não foi possível carregar as ordens',
    'orders.empty_title': 'Nenhuma ordem {status}',
    'orders.empty_intro':
      'Crie uma ordem limite em qualquer mercado pra reservar um preço. Ordens abertas aparecem aqui até serem executadas ou canceladas.',
    'orders.empty_cta_markets': 'Ver mercados',
    'orders.filter_all': 'Todas',
    'orders.filter_open': 'Abertas',
    'orders.filter_pending': 'Pendentes',
    'orders.filter_matched': 'Executadas',
    'orders.filter_cancelled': 'Canceladas',
    'orders.filter_failed': 'Falhas',
    'orders.status_pending': 'Pendente',
    'orders.status_open': 'Aberta',
    'orders.status_matched': 'Executada',
    'orders.status_cancelled': 'Cancelada',
    'orders.status_failed': 'Falha',
    'orders.side_buy': 'COMPRA',
    'orders.side_sell': 'VENDA',
    'orders.size_label': 'Tamanho',
    'orders.price_label': 'Preço',
    'orders.total_label': 'Total',
    'orders.created_label': 'Enviada',
    'orders.type_label': 'Tipo',
    'orders.cancel_btn': 'Cancelar ordem',
    'orders.cancelling': 'Cancelando…',
    'orders.cancel_confirm': 'Cancelar esta ordem? Não dá pra desfazer.',
    'orders.cancel_failed_title': 'Não foi possível cancelar a ordem',
    'orders.view_market': 'Ver mercado',

    'ranking.eyebrow': 'Ranking',
    'ranking.title': 'Top traders',
    'ranking.description': 'Usuários com maior volume no período.',
    'ranking.breadcrumb': 'Ranking',
    'ranking.loading_title': 'Carregando ranking',
    'ranking.loading_desc': 'Agregando volume do período selecionado.',
    'ranking.loading_short': 'Carregando ranking…',
    'ranking.empty_title': 'Sem traders ainda',
    'ranking.empty_desc': 'Nenhuma trade registrada nesse período.',
    'ranking.error_title': 'Não foi possível carregar o ranking',
    'ranking.period_day': '24h',
    'ranking.period_week': 'Semana',
    'ranking.period_month': 'Mês',
    'ranking.period_all': 'Histórico',
    'ranking.volume_label': 'Volume',
    'ranking.trades_label': 'Trades',
    'ranking.you_badge': 'Você',
    'ranking.anonymous': 'Anônimo',

    'copy.eyebrow': 'Copy trading',
    'copy.title': 'Seguindo',
    'copy.description': 'Receba notificação quando esses líderes operarem. Execução automática em breve.',
    'copy.breadcrumb': 'Copy',
    'copy.loading_title': 'Carregando seguidores',
    'copy.loading_desc': 'Buscando os líderes que você segue.',
    'copy.loading_short': 'Carregando…',
    'copy.empty_title': 'Você não segue ninguém',
    'copy.empty_desc': 'Adicione o endereço de um líder pra ser avisado quando ele operar.',
    'copy.add_title': 'Seguir um líder',
    'copy.add_address_label': 'Endereço do líder',
    'copy.add_address_placeholder': '0x…',
    'copy.add_max_label': 'Máximo USD por trade (opcional)',
    'copy.add_mult_label': 'Multiplicador de tamanho (opcional)',
    'copy.add_btn': 'Seguir líder',
    'copy.adding': 'Seguindo…',
    'copy.add_error_title': 'Não foi possível seguir',
    'copy.invalid_address': 'Use um endereço 0x válido (40 caracteres hex).',
    'copy.unfollow_btn': 'Deixar de seguir',
    'copy.unfollow_confirm': 'Deixar de seguir esse líder?',
    'copy.unfollowing': 'Removendo…',
    'copy.unfollow_error_title': 'Não foi possível deixar de seguir',
    'copy.row_max': 'Máx ${max}',
    'copy.row_mult': '{mult}×',
    'copy.row_since': 'Seguindo desde {date}',
    'copy.active': 'Ativo',
    'copy.inactive': 'Inativo',
    'copy.notice_title': 'Só notificação',
    'copy.notice_desc': 'Você é avisado quando o líder opera. Execução automática chega com session keys.',
    'copy.top_title': 'Top traders',
    'copy.top_loading': 'Carregando top traders…',
    'copy.top_empty': 'Nenhum trader ainda. Volte em breve.',
    'copy.top_volume': '{vol} vol',
    'copy.top_pnl': '{pnl} PnL',
    'copy.leader.breadcrumb': 'Trader',
    'copy.leader.eyebrow': 'Trader',
    'copy.leader.loading_title': 'Carregando trader',
    'copy.leader.loading_desc': 'Buscando a atividade recente deste trader.',
    'copy.leader.loading_short': 'Carregando…',
    'copy.leader.title': 'Perfil do trader',
    'copy.leader.load_failed': 'Não foi possível carregar o trader',
    'copy.leader.not_found': 'Trader não encontrado.',
    'copy.leader.how_title': 'Como funciona o copy',
    'copy.leader.how_desc': 'Avisamos no Telegram as últimas movimentações deste trader. Cada alerta tem um botão para abrir o app e copiar a ordem em um toque.',
    'copy.leader.follow_title': 'Seguir e receber alertas',
    'copy.leader.follow_btn': 'Seguir trader',
    'copy.leader.recent_title': 'Trades recentes',
    'copy.leader.recent_empty': 'Sem trades recentes.',
    'copy.leader.just_now': 'agora',
    'copy.leader.mins_ago': 'há {n}min',
    'copy.leader.hours_ago': 'há {n}h',
    'copy.leader.days_ago': 'há {n}d',
    'copy.confirm.breadcrumb': 'Copiar',
    'copy.confirm.eyebrow': 'Copiar em 1 clique',
    'copy.confirm.loading_title': 'Carregando sinal',
    'copy.confirm.loading_desc': 'Buscando a operação a copiar.',
    'copy.confirm.loading_short': 'Carregando…',
    'copy.confirm.title': 'Copiar esta operação',
    'copy.confirm.description': 'Replicar a jogada de {leader}.',
    'copy.confirm.load_failed': 'Não foi possível carregar o sinal',
    'copy.confirm.not_found': 'Sinal não encontrado ou expirado.',
    'copy.confirm.side_buy': 'Compra',
    'copy.confirm.side_sell': 'Venda',
    'copy.confirm.field_outcome': 'Resultado',
    'copy.confirm.field_price': 'Preço',
    'copy.confirm.field_leader': 'Trader',
    'copy.confirm.field_status': 'Status',
    'copy.confirm.amount_title': 'Valor',
    'copy.confirm.amount_label': 'USD a investir',
    'copy.confirm.action_error': 'Ação falhou',
    'copy.confirm.handled_title': 'Já tratado',
    'copy.confirm.handled_desc': 'Este sinal já foi executado ou descartado.',
    'copy.confirm.confirming': 'Preparando…',
    'copy.confirm.confirm_btn': 'Copiar agora',
    'copy.confirm.dismiss_btn': 'Descartar',

    'help.eyebrow': 'Ajuda',
    'help.title': 'Como usar a Torian',
    'help.description': 'Referência rápida pro Mini App e comandos do bot.',
    'help.breadcrumb': 'Ajuda',
    'help.section_app': 'Mini App',
    'help.section_bot': 'Comandos do bot',
    'help.section_links': 'Recursos',
    'help.app.markets_label': 'Mercados',
    'help.app.markets_desc': 'Explore categorias e busque mercados na Polymarket.',
    'help.app.portfolio_label': 'Portfólio',
    'help.app.portfolio_desc': 'Suas posições abertas e o valor total.',
    'help.app.orders_label': 'Ordens',
    'help.app.orders_desc': 'Suas ordens limite ativas e histórico.',
    'help.app.deposit_label': 'Depositar / Bridge',
    'help.app.deposit_desc': 'Carregue pUSD via USDC na Polygon ou pelo Polymarket Bridge.',
    'help.app.withdraw_label': 'Sacar',
    'help.app.withdraw_desc': 'Tire pUSD de volta pra sua carteira na Polygon.',
    'help.app.ranking_label': 'Ranking',
    'help.app.ranking_desc': 'Top traders por volume.',
    'help.app.copy_label': 'Copy trading',
    'help.app.copy_desc': 'Siga líderes e receba avisos quando eles operarem.',
    'help.bot.market': '`/market <termo>` — buscar mercados',
    'help.bot.portfolio': '`/portfolio` — seu portfólio',
    'help.bot.wallet': '`/wallet` — sua carteira',
    'help.bot.deposit': '`/deposit` — depositar',
    'help.bot.withdraw': '`/withdraw` — sacar',
    'help.bot.whale': '`/whale` — trades de baleia recentes',
    'help.bot.copy': '`/copy` — seguir um líder (só notificação)',
    'help.bot.ranking': '`/ranking` — top traders',
    'help.bot.lang': '`/lang` — trocar idioma',
    'help.bot.help': '`/help` — mostrar essa mensagem',
    'help.links.polymarket': 'Abrir Polymarket',
    'help.links.support': 'Falar com suporte',

    'settings.eyebrow': 'Ajustes',
    'settings.title': 'Ajustes',
    'settings.description': 'Gerencie as preferências do Mini App.',
    'settings.breadcrumb': 'Ajustes',
    'deposit.breadcrumb': 'Depósito',
    'withdraw.breadcrumb': 'Sacar',
    'swap.breadcrumb': 'Conversor',
    'deploy.breadcrumb': 'Deploy',
    'rescue.breadcrumb': 'Resgate',
    'approvals.breadcrumb': 'Aprovações',
    'bridge.breadcrumb': 'Bridge',
    'sign_order.breadcrumb': 'Assinar ordem',
    'settings.lang_title': 'Idioma',
    'settings.lang_desc': 'Usado no Mini App. O bot mantém o ajuste próprio.',
    'settings.lang_auto': 'Auto (do Telegram)',
    'settings.lang_saved': 'Idioma atualizado.',
    'settings.address_title': 'Carteira',
    'settings.address_desc': 'Seu endereço de depósito na Polygon.',
    'settings.user_id_label': 'User ID',
    'settings.session_title': 'Sessão',
    'settings.signed_in_as': 'Logado como',
    'settings.about_title': 'Sobre',
    'settings.version_label': 'Versão',
    'settings.reset_lang_btn': 'Resetar idioma',
  },
  es: {
    'common.loading': 'Cargando…',
    'common.back_to_chat': 'Volver al chat',
    'common.copy_address': 'Copiar dirección',
    'common.copy_full_address': 'Copiar dirección completa',
    'common.copied': '✓ Copiado',
    'common.auth_error': 'Error de autenticación: {error}',
    'common.error': 'Error: {message}',
    'common.balance': 'Saldo',

    'home.title': 'Torian',
    'home.ready': 'Mini App lista.',
    'home.loading_title': 'Preparando tu Mini App',
    'home.loading_desc': 'Autenticando tu sesión de Telegram y sincronizando tu billetera Polymarket.',
    'home.loading_short': 'Autenticando tu sesión.',
    'home.auth_eyebrow': 'Auth',
    'home.auth_failed': 'Fallo de autenticación',
    'home.wallet': 'Cartera: {address}',
    'home.foreign.title': 'Fondos en el token equivocado',
    'home.foreign.body':
      'Detectamos tokens en tu cartera que Polymarket no acepta para operar. Solo <b>pUSD</b> (o <b>USDC</b>/<b>USDC.e</b> vía Bridge) aparece como saldo.',
    'home.foreign.list_label': 'Detectado:',
    'home.foreign.cta':
      'Puedes rescatar estos fondos tú mismo, sin gas — no necesitas soporte.',
    'home.foreign.cta_pusd': 'Convertir a pUSD',
    'home.foreign.cta_eoa': 'Enviar a mi cartera',
    'home.balance_title': 'Saldo pUSD',
    'home.balance_loading': 'Cargando…',
    'home.deploy_wallet_cta': 'Implementar cartera (sin gas)',
    'home.section_browse': 'Explorar',
    'home.section_social': 'Social',
    'home.section_money': 'Dinero',
    'home.tile.markets_title': 'Mercados',
    'home.tile.markets_desc': 'Categorías de Polymarket.',
    'home.tile.portfolio_title': 'Portafolio',
    'home.tile.portfolio_desc': 'Tus posiciones abiertas.',
    'home.tile.orders_title': 'Órdenes',
    'home.tile.orders_desc': 'Órdenes límite en el libro.',
    'home.tile.ranking_title': 'Ranking',
    'home.tile.ranking_desc': 'Top traders por volumen.',
    'home.tile.copy_title': 'Copy trading',
    'home.tile.copy_desc': 'Sigue líderes, recibe avisos.',
    'home.tile.deposit_title': 'Depositar',
    'home.tile.deposit_desc': 'Recargar tu saldo pUSD.',
    'home.tile.withdraw_title': 'Retirar',
    'home.tile.withdraw_desc': 'Sacar pUSD de Polymarket.',
    'home.tile.swap_title': 'Convertir',
    'home.tile.swap_desc': 'USDC → pUSD sin gas.',
    'home.tile.bridge_title': 'Bridge',
    'home.tile.bridge_desc': 'Depósitos entre cadenas.',
    'home.tile.approvals_title': 'Habilitar trading',
    'home.tile.approvals_desc': 'Configuración on-chain (1 vez).',

    'rescue.preparing_title': 'Preparando rescate',
    'rescue.preparing_desc':
      'Autenticando tu sesión de Telegram antes de escanear la cartera.',
    'rescue.preparing_short': 'Autenticando…',
    'rescue.scanning_title': 'Escaneando tu cartera',
    'rescue.scanning_desc':
      'Leyendo saldos on-chain y armando el batch de rescate sin gas.',
    'rescue.scanning_short': 'Escaneando…',
    'rescue.failed': 'Rescate falló',
    'rescue.detected_label': 'Detectado',
    'rescue.detected_title': 'Tokens a rescatar',
    'rescue.dest_label': 'Destino',
    'rescue.gas_label': 'Gas',
    'rescue.signing': 'Firmando…',
    'rescue.submitting': 'Enviando…',
    'rescue.submitted_title': 'Rescate enviado',
    'rescue.submitted_badge': 'Enviado',
    'rescue.submitted_body': 'Tu rescate está en el relayer de Polymarket',
    'rescue.submitted_desc':
      'La confirmación suele llegar en ~30 segundos. Los saldos se actualizan solos.',
    'rescue.state_label': 'Estado',
    'rescue.view_on_polygonscan': 'Ver en Polygonscan',
    'rescue.eoa.title': 'Enviar fondos a mi cartera',
    'rescue.eoa.desc':
      'Devuelve POL / WMATIC / USDT desde la deposit wallet a tu EOA. Sin gas, vía relayer de Polymarket.',
    'rescue.eoa.cta': 'Firmar y enviar a mi cartera',
    'rescue.pusd.title': 'Convertir a pUSD',
    'rescue.pusd.desc':
      'Hace wrap del POL nativo y convierte todo a pUSD vía Uniswap V3, en un solo batch sin gas. El resultado cae como pUSD operable en tu deposit wallet.',
    'rescue.pusd.cta': 'Firmar y convertir a pUSD',
    'rescue.pusd.dest_value': 'pUSD en tu deposit wallet',

    'deploy.title': 'Configuración de la cartera',
    'deploy.creating': 'Creando tu cartera…',
    'deploy.init_failed': 'No se pudo inicializar la cartera.',
    'deploy.ready_blurb':
      'Tu cartera está lista. Polymarket usa una smart-wallet (proxy) en Polygon — el proxy se despliega automáticamente en tu primer depósito.',
    'deploy.your_address': 'Tu dirección (EOA)',
    'deploy.backed_by': 'Respaldada por {backend}',
    'deploy.backend_web3auth': 'Web3Auth MPC',
    'deploy.backend_local': 'clave local (modo dev)',
    'deploy.done': 'Listo — volver al chat',
    'deploy.close_hint':
      'Puedes cerrar esta ventana con el ✕ de arriba.',

    'deposit.title': 'Depositar',
    'deposit.blurb_bridge':
      'Envía <b>USDC</b> o <b>USDC.e</b> en <b>Polygon</b> a la dirección puente de abajo. Se convertirá en pUSD y se acreditará automáticamente. Mínimo $2.',
    'deposit.blurb_direct':
      'Envía <b>pUSD</b> en <b>Polygon</b> a la dirección de abajo.',
    'deposit.bridge_label': 'Polymarket Bridge (Polygon)',
    'deposit.direct_label': 'Tu dirección de depósito',
    'deposit.not_deployed_title': '⚠ Cartera aún no desplegada.',
    'deposit.not_deployed_body':
      'El contrato se despliega en el primer uso. Puedes depositar antes, pero operar requiere despliegue.',
    'deposit.deploy_btn': 'Desplegar ahora (sin gas)',
    'deposit.deploying': 'Desplegando…',
    'deposit.footer_bridge':
      'Solo Polygon. Soporta USDC, USDC.e, USDT, DAI, WETH, POL, SAND. Otras redes vía Polymarket Bridge.',
    'deposit.footer_direct':
      'Envía solo pUSD en Polygon. Enviar otros tokens aquí puede causar pérdida permanente.',
    'deposit.intent_buy_title': 'Completa el depósito para continuar tu compra',
    'deposit.intent_buy_body':
      'Querías comprar <b>{outcome}</b> por <b>${usd}</b>. Necesitas al menos <b>${required}</b> en tu wallet.',
    'deposit.intent_progress': 'Saldo: <b>${balance}</b> / ${required}',
    'deposit.intent_short': 'Faltan <b>${missing}</b>',
    'deposit.intent_ready_title': '✅ Listo para operar',
    'deposit.intent_ready_body':
      'Saldo suficiente. Vuelve al chat y toca el mismo botón para reintentar la compra.',
    'deposit.intent_back_to_chat': 'Volver al chat',
    'deposit.intent_continue_button': 'Continuar compra',
    'deposit.intent_continuing': 'Preparando orden…',
    'deposit.loading_title': 'Preparando depósito',
    'deposit.loading_desc':
      'Autenticando tu sesión de Telegram y resolviendo la dirección del puente.',
    'deposit.loading_short': 'Resolviendo la dirección del puente.',
    'deposit.auth_failed': 'Fallo de autenticación',
    'deposit.load_failed': 'No se pudo cargar tu billetera',
    'deposit.qr_alt': 'Código QR del depósito',
    'deposit.qr_card_title': 'Escanea o copia para enviar fondos',
    'deposit.qr_card_desc':
      'El QR codifica solo la dirección de destino — define el monto en tu app de envío.',
    'deposit.field_network': 'Red',
    'deposit.field_token': 'Token',
    'deposit.field_status': 'Estado',
    'deposit.status_deployed': 'Desplegado',
    'deposit.status_not_deployed': 'No desplegado',
    'deposit.action_required': 'Acción requerida',

    'withdraw.title': 'Retirar pUSD',
    'withdraw.title_sent': 'Retiro enviado',
    'withdraw.title_confirm': 'Confirmar retiro',
    'withdraw.authenticating': 'Autenticando…',
    'withdraw.loading_balance': 'Cargando saldo…',
    'withdraw.sent_blurb':
      'Enviado <b>${amount}</b> pUSD a {to}. Suele llegar en segundos.',
    'withdraw.state': 'Estado',
    'withdraw.view_polygonscan': 'Ver en Polygonscan',
    'withdraw.available': 'Disponible',
    'withdraw.daily_limit': 'Límite diario: ${limit}',
    'withdraw.daily_limit_label': 'Límite diario',
    'withdraw.remaining_today': 'Restante hoy: ${remaining}',
    'withdraw.destination_label': 'Destino (dirección Polygon)',
    'withdraw.destination_warning':
      'Solo red Polygon (MATIC). Enviar a red incorrecta = pérdida de fondos.',
    'withdraw.amount_label': 'Monto (pUSD)',
    'withdraw.max': 'Máx',
    'withdraw.exceeds_balance': 'Excede saldo (${amount})',
    'withdraw.exceeds_daily': 'Excede restante diario (${amount})',
    'withdraw.enter_positive': 'Ingresa un valor positivo',
    'withdraw.preparing': 'Preparando…',
    'withdraw.review': 'Revisar',
    'withdraw.to': 'Para',
    'withdraw.amount': 'Monto',
    'withdraw.network_fee': 'Tarifa de red',
    'withdraw.gasless': 'Sin gas (patrocinado)',
    'withdraw.remaining_after': 'Restante después',
    'withdraw.sign_send': 'Firmar y enviar',
    'withdraw.signing': 'Firmando…',
    'withdraw.submitting': 'Enviando…',
    'withdraw.back': 'Volver',

    'swap.eyebrow': 'Swap',
    'swap.title': 'Convertir USDC → pUSD',
    'swap.description':
      'Las operaciones de Polymarket se liquidan en pUSD. Convierte el USDC de tu cartera — sin gas, vía relayer de Polymarket.',
    'swap.preparing_title': 'Preparando el swap',
    'swap.preparing_desc':
      'Autenticando tu sesión de Telegram antes de cotizar USDC → pUSD.',
    'swap.preparing_short': 'Autenticando sesión.',
    'swap.quoting_title': 'Cotizando USDC → pUSD',
    'swap.quoting_desc':
      'Pidiendo al relayer de Polymarket una tasa de conversión actualizada.',
    'swap.quoting_short': 'Cotizando el swap.',
    'swap.step_session': 'Sesión',
    'swap.step_wallet': 'Cartera',
    'swap.step_relayer': 'Relayer',
    'swap.quote_label': 'Cotización',
    'swap.from_label': 'Desde',
    'swap.to_min_label': 'Hacia (mín)',
    'swap.wallet_label': 'Cartera',
    'swap.gas_label': 'Gas',
    'swap.gasless': 'Sin gas',
    'swap.usdc_on_polygon': 'USDC en Polygon',
    'swap.cta_sign': 'Firmar y convertir',
    'swap.signing': 'Firmando…',
    'swap.submitting': 'Enviando…',
    'swap.failed_title': 'Swap falló',
    'swap.auth_failed_title': 'Fallo de autenticación',
    'swap.no_balance_title': 'Sin USDC para convertir',
    'swap.no_balance_body':
      'Tu cartera no tiene saldo de USDC ni USDC.e para hacer el swap. Deposita USDC primero e inténtalo de nuevo.',
    'swap.cta_deposit': 'Depositar USDC',
    'swap.submitted_eyebrow': 'Enviado',
    'swap.submitted_title': 'Swap enviado',
    'swap.submitted_body': 'Tu conversión está en el relayer de Polymarket',
    'swap.submitted_desc':
      'La confirmación suele llegar en ~30 segundos. Tu saldo de pUSD se actualizará solo.',
    'swap.state_label': 'Estado',
    'swap.view_polygonscan': 'Ver en Polygonscan',

    'market.eyebrow': 'Mercados',
    'market.title': 'Explorar mercados',
    'market.description':
      'Elige una categoría para ver preguntas en vivo de Polymarket, o busca por nombre.',
    'market.breadcrumb': 'Mercados',
    'market.search_label': 'Buscar',
    'market.search_placeholder': 'p. ej.: Trump, Mundial, Bitcoin…',
    'market.search_cta': 'Buscar',
    'market.categories_title': 'Categorías',
    'market.categories_description':
      'Dieciséis temas curados, actualizados en vivo desde Polymarket.',
    'market.cat.breaking': 'Últimas',
    'market.cat.politics': 'Política',
    'market.cat.trump': 'Trump',
    'market.cat.crypto': 'Cripto',
    'market.cat.sports': 'Deportes',
    'market.cat.elections': 'Elecciones',
    'market.cat.world': 'Mundo',
    'market.cat.business': 'Negocios',
    'market.cat.geopolitics': 'Geopolítica',
    'market.cat.finance': 'Finanzas',
    'market.cat.tech': 'Tecnología',
    'market.cat.culture': 'Cultura',
    'market.cat.economy': 'Economía',
    'market.cat.mentions': 'Menciones',
    'market.cat.climate': 'Clima',
    'market.cat.bonds': 'Bonos',

    'market.list.eyebrow_cat': '{category}',
    'market.list.eyebrow_search': 'Búsqueda',
    'market.list.title_cat': '{category}',
    'market.list.title_search': '“{term}”',
    'market.list.description_cat':
      'Mercados en vivo de esta categoría, actualizados desde Polymarket.',
    'market.list.description_search':
      'Mercados que coinciden con tu búsqueda en todas las categorías.',
    'market.list.breadcrumb_cat': '{category}',
    'market.list.breadcrumb_search': 'Búsqueda: {term}',
    'market.list.loading_title': 'Cargando mercados…',
    'market.list.loading_desc':
      'Obteniendo las preguntas y precios más recientes de Polymarket.',
    'market.list.loading_short': 'Cargando mercados.',
    'market.list.error_title': 'No se pudieron cargar los mercados',
    'market.list.empty_title': 'No se encontraron mercados',
    'market.list.empty_cat':
      'No hay mercados activos en esta categoría en este momento. Prueba otra categoría o vuelve más tarde.',
    'market.list.empty_search':
      'Ningún mercado coincide con “{term}”. Prueba otra palabra clave o navega por categoría.',
    'market.list.cta_categories': 'Ver categorías',
    'market.list.volume24h': 'vol 24h',
    'market.list.no_volume': '—',
    'market.list.ends': 'Termina {date}',
    'market.list.prev': 'Anterior',
    'market.list.next': 'Siguiente',
    'market.list.page_of': 'Página {page} de {total}',

    'market.detail.eyebrow': 'Mercado',
    'market.detail.breadcrumb': 'Mercado',
    'market.detail.loading_title': 'Cargando mercado…',
    'market.detail.loading_desc':
      'Obteniendo precios, resultados y vista previa del libro de órdenes de Polymarket.',
    'market.detail.loading_short': 'Cargando mercado.',
    'market.detail.error_title': 'No se pudo cargar el mercado',
    'market.detail.outcomes_title': 'Resultados',
    'market.detail.outcomes_description':
      'Toca un monto para preparar una orden de COMPRA. Revisarás y firmarás en la siguiente pantalla.',
    'market.detail.preview_title': 'Vista previa del libro',
    'market.detail.preview_description':
      'Mejor compra / venta del CLOB. La ejecución real depende de la liquidez en vivo.',
    'market.detail.bestbid_label': 'Mejor compra',
    'market.detail.bestask_label': 'Mejor venta',
    'market.detail.spread_label': 'Spread',
    'market.detail.volume24h_label': 'Volumen 24h',
    'market.detail.volume_total_label': 'Volumen total',
    'market.detail.liquidity_label': 'Liquidez',
    'market.detail.ends_label': 'Termina',
    'market.detail.about_title': 'Sobre este mercado',
    'market.detail.buy_for': 'Comprar {outcome} · ${amount}',
    'market.detail.drafting': 'Creando…',
    'market.detail.min_order': 'Mínimo: ${amount}',
    'market.detail.auth_required':
      'Auténticate en tu sesión de Telegram para abrir órdenes.',
    'market.detail.draft_failed_title': 'No se pudo crear la orden',
    'market.detail.no_balance_title': 'Saldo pUSD insuficiente',
    'market.detail.no_balance_body': 'Tienes ${have} y esta orden necesita ${need}. Recarga tu billetera y vuelve a intentarlo.',
    'market.detail.deposit_cta': 'Depositar pUSD',
    'market.detail.approvals_title': 'Primero habilita el trading',
    'market.detail.approvals_body':
      'Antes de tu primera orden necesitas una configuración única que autoriza a Polymarket a mover los fondos de tu billetera de depósito. Es sin gas y solo se hace una vez.',
    'market.detail.approvals_cta': 'Habilitar trading',

    'portfolio.eyebrow': 'Portafolio',
    'portfolio.breadcrumb': 'Portafolio',
    'portfolio.title': 'Tu portafolio',
    'portfolio.description':
      'Posiciones abiertas en Polymarket. Cada cuota paga $1 si tu lado gana.',
    'portfolio.loading_title': 'Cargando tu portafolio…',
    'portfolio.loading_desc':
      'Obteniendo posiciones abiertas y valores actuales de Polymarket.',
    'portfolio.loading_short': 'Cargando posiciones.',
    'portfolio.error_title': 'No se pudo cargar el portafolio',
    'portfolio.empty_title': 'Aún no tienes posiciones',
    'portfolio.empty_intro': 'Haz tu primera predicción en 3 pasos:',
    'portfolio.empty_step_1':
      '1. Deposita pUSD — financia tu cartera (USDC, USDT o pUSD).',
    'portfolio.empty_step_2':
      '2. Explora mercados — elige una pregunta que te interese.',
    'portfolio.empty_step_3':
      '3. Compra YES o NO — cada cuota paga $1 si tu lado gana.',
    'portfolio.empty_cta_deposit': 'Depositar pUSD',
    'portfolio.empty_cta_markets': 'Explorar mercados',
    'portfolio.empty_cta_help': 'Cómo funciona',
    'portfolio.totals_title': 'Totales',
    'portfolio.payout_label': 'Pago máximo',
    'portfolio.value_label': 'Valor actual',
    'portfolio.size_label': 'Cuotas',
    'portfolio.avg_label': 'Prom.',
    'portfolio.cur_label': 'Ahora',
    'portfolio.pnl_label': 'PnL',
    'portfolio.resolves_label': 'Resuelve',
    'portfolio.redeemable_badge': 'Redimible',
    'portfolio.cta_open_market': 'Ver mercado',

    'orders.eyebrow': 'Órdenes',
    'orders.breadcrumb': 'Órdenes',
    'orders.title': 'Órdenes abiertas',
    'orders.description':
      'Órdenes límite en el libro de Polymarket. Toca una tarjeta para ver el mercado o cancelar.',
    'orders.loading_title': 'Cargando tus órdenes…',
    'orders.loading_desc': 'Obteniendo órdenes abiertas del CLOB de Polymarket.',
    'orders.loading_short': 'Cargando órdenes.',
    'orders.error_title': 'No se pudieron cargar las órdenes',
    'orders.empty_title': 'Sin órdenes {status}',
    'orders.empty_intro':
      'Crea una orden límite en cualquier mercado para reservar un precio. Las órdenes abiertas aparecen aquí hasta que se ejecuten o las canceles.',
    'orders.empty_cta_markets': 'Ver mercados',
    'orders.filter_all': 'Todas',
    'orders.filter_open': 'Abiertas',
    'orders.filter_pending': 'Pendientes',
    'orders.filter_matched': 'Ejecutadas',
    'orders.filter_cancelled': 'Canceladas',
    'orders.filter_failed': 'Fallidas',
    'orders.status_pending': 'Pendiente',
    'orders.status_open': 'Abierta',
    'orders.status_matched': 'Ejecutada',
    'orders.status_cancelled': 'Cancelada',
    'orders.status_failed': 'Fallida',
    'orders.side_buy': 'COMPRA',
    'orders.side_sell': 'VENTA',
    'orders.size_label': 'Tamaño',
    'orders.price_label': 'Precio',
    'orders.total_label': 'Total',
    'orders.created_label': 'Enviada',
    'orders.type_label': 'Tipo',
    'orders.cancel_btn': 'Cancelar orden',
    'orders.cancelling': 'Cancelando…',
    'orders.cancel_confirm': '¿Cancelar esta orden? No se puede deshacer.',
    'orders.cancel_failed_title': 'No se pudo cancelar la orden',
    'orders.view_market': 'Ver mercado',

    'ranking.eyebrow': 'Ranking',
    'ranking.title': 'Top traders',
    'ranking.description': 'Usuarios con mayor volumen en el período.',
    'ranking.breadcrumb': 'Ranking',
    'ranking.loading_title': 'Cargando ranking',
    'ranking.loading_desc': 'Agregando volumen del período seleccionado.',
    'ranking.loading_short': 'Cargando ranking…',
    'ranking.empty_title': 'Aún no hay traders',
    'ranking.empty_desc': 'No hay operaciones registradas en este período.',
    'ranking.error_title': 'No se pudo cargar el ranking',
    'ranking.period_day': '24h',
    'ranking.period_week': 'Semana',
    'ranking.period_month': 'Mes',
    'ranking.period_all': 'Histórico',
    'ranking.volume_label': 'Volumen',
    'ranking.trades_label': 'Operaciones',
    'ranking.you_badge': 'Tú',
    'ranking.anonymous': 'Anónimo',

    'copy.eyebrow': 'Copy trading',
    'copy.title': 'Siguiendo',
    'copy.description': 'Recibe notificaciones cuando estos líderes operen. Ejecución automática pronto.',
    'copy.breadcrumb': 'Copy',
    'copy.loading_title': 'Cargando seguidos',
    'copy.loading_desc': 'Buscando los líderes que sigues.',
    'copy.loading_short': 'Cargando…',
    'copy.empty_title': 'No sigues a nadie',
    'copy.empty_desc': 'Agrega la dirección de un líder para recibir avisos cuando opere.',
    'copy.add_title': 'Seguir a un líder',
    'copy.add_address_label': 'Dirección del líder',
    'copy.add_address_placeholder': '0x…',
    'copy.add_max_label': 'Máximo USD por operación (opcional)',
    'copy.add_mult_label': 'Multiplicador de tamaño (opcional)',
    'copy.add_btn': 'Seguir líder',
    'copy.adding': 'Siguiendo…',
    'copy.add_error_title': 'No se pudo seguir',
    'copy.invalid_address': 'Usa una dirección 0x válida (40 hex).',
    'copy.unfollow_btn': 'Dejar de seguir',
    'copy.unfollow_confirm': '¿Dejar de seguir a este líder?',
    'copy.unfollowing': 'Quitando…',
    'copy.unfollow_error_title': 'No se pudo dejar de seguir',
    'copy.row_max': 'Máx ${max}',
    'copy.row_mult': '{mult}×',
    'copy.row_since': 'Siguiendo desde {date}',
    'copy.active': 'Activo',
    'copy.inactive': 'Inactivo',
    'copy.notice_title': 'Sólo notificaciones',
    'copy.notice_desc': 'Te avisamos cuando el líder opera. La ejecución automática llega con session keys.',
    'copy.top_title': 'Top traders',
    'copy.top_loading': 'Cargando top traders…',
    'copy.top_empty': 'Aún no hay traders. Vuelve pronto.',
    'copy.top_volume': '{vol} vol',
    'copy.top_pnl': '{pnl} PnL',
    'copy.leader.breadcrumb': 'Trader',
    'copy.leader.eyebrow': 'Trader',
    'copy.leader.loading_title': 'Cargando trader',
    'copy.leader.loading_desc': 'Obteniendo la actividad reciente de este trader.',
    'copy.leader.loading_short': 'Cargando…',
    'copy.leader.title': 'Perfil del trader',
    'copy.leader.load_failed': 'No se pudo cargar el trader',
    'copy.leader.not_found': 'Trader no encontrado.',
    'copy.leader.how_title': 'Cómo funciona el copy',
    'copy.leader.how_desc': 'Te avisamos por Telegram los últimos movimientos de este trader. Cada alerta tiene un botón para abrir la app y copiar la orden con un toque.',
    'copy.leader.follow_title': 'Seguir y recibir alertas',
    'copy.leader.follow_btn': 'Seguir trader',
    'copy.leader.recent_title': 'Operaciones recientes',
    'copy.leader.recent_empty': 'Sin operaciones recientes.',
    'copy.leader.just_now': 'ahora',
    'copy.leader.mins_ago': 'hace {n}min',
    'copy.leader.hours_ago': 'hace {n}h',
    'copy.leader.days_ago': 'hace {n}d',
    'copy.confirm.breadcrumb': 'Copiar',
    'copy.confirm.eyebrow': 'Copiar en 1 clic',
    'copy.confirm.loading_title': 'Cargando señal',
    'copy.confirm.loading_desc': 'Obteniendo la operación a copiar.',
    'copy.confirm.loading_short': 'Cargando…',
    'copy.confirm.title': 'Copiar esta operación',
    'copy.confirm.description': 'Replica la jugada de {leader}.',
    'copy.confirm.load_failed': 'No se pudo cargar la señal',
    'copy.confirm.not_found': 'Señal no encontrada o expirada.',
    'copy.confirm.side_buy': 'Compra',
    'copy.confirm.side_sell': 'Venta',
    'copy.confirm.field_outcome': 'Resultado',
    'copy.confirm.field_price': 'Precio',
    'copy.confirm.field_leader': 'Trader',
    'copy.confirm.field_status': 'Estado',
    'copy.confirm.amount_title': 'Monto',
    'copy.confirm.amount_label': 'USD a invertir',
    'copy.confirm.action_error': 'La acción falló',
    'copy.confirm.handled_title': 'Ya gestionado',
    'copy.confirm.handled_desc': 'Esta señal ya fue ejecutada o descartada.',
    'copy.confirm.confirming': 'Preparando…',
    'copy.confirm.confirm_btn': 'Copiar ahora',
    'copy.confirm.dismiss_btn': 'Descartar',

    'help.eyebrow': 'Ayuda',
    'help.title': 'Cómo usar Torian',
    'help.description': 'Referencia rápida del Mini App y los comandos del bot.',
    'help.breadcrumb': 'Ayuda',
    'help.section_app': 'Mini App',
    'help.section_bot': 'Comandos del bot',
    'help.section_links': 'Recursos',
    'help.app.markets_label': 'Mercados',
    'help.app.markets_desc': 'Explora categorías y busca mercados de Polymarket.',
    'help.app.portfolio_label': 'Portafolio',
    'help.app.portfolio_desc': 'Tus posiciones abiertas y valor total.',
    'help.app.orders_label': 'Órdenes',
    'help.app.orders_desc': 'Tus órdenes límite activas e histórico.',
    'help.app.deposit_label': 'Depositar / Bridge',
    'help.app.deposit_desc': 'Carga pUSD con USDC en Polygon o por el Polymarket Bridge.',
    'help.app.withdraw_label': 'Retirar',
    'help.app.withdraw_desc': 'Saca pUSD de vuelta a tu cartera en Polygon.',
    'help.app.ranking_label': 'Ranking',
    'help.app.ranking_desc': 'Top traders por volumen.',
    'help.app.copy_label': 'Copy trading',
    'help.app.copy_desc': 'Sigue líderes y recibe avisos cuando operen.',
    'help.bot.market': '`/market <término>` — buscar mercados',
    'help.bot.portfolio': '`/portfolio` — tu portafolio',
    'help.bot.wallet': '`/wallet` — tu cartera',
    'help.bot.deposit': '`/deposit` — depositar fondos',
    'help.bot.withdraw': '`/withdraw` — retirar',
    'help.bot.whale': '`/whale` — operaciones de ballena recientes',
    'help.bot.copy': '`/copy` — seguir a un líder (sólo aviso)',
    'help.bot.ranking': '`/ranking` — top traders',
    'help.bot.lang': '`/lang` — cambiar idioma',
    'help.bot.help': '`/help` — mostrar esta ayuda',
    'help.links.polymarket': 'Abrir Polymarket',
    'help.links.support': 'Contactar soporte',

    'settings.eyebrow': 'Ajustes',
    'settings.title': 'Ajustes',
    'settings.description': 'Administra las preferencias del Mini App.',
    'settings.breadcrumb': 'Ajustes',
    'deposit.breadcrumb': 'Depósito',
    'withdraw.breadcrumb': 'Retirar',
    'swap.breadcrumb': 'Conversor',
    'deploy.breadcrumb': 'Deploy',
    'rescue.breadcrumb': 'Rescate',
    'approvals.breadcrumb': 'Aprobaciones',
    'bridge.breadcrumb': 'Bridge',
    'sign_order.breadcrumb': 'Firmar orden',
    'settings.lang_title': 'Idioma',
    'settings.lang_desc': 'Usado en el Mini App. El bot mantiene su propio ajuste.',
    'settings.lang_auto': 'Auto (desde Telegram)',
    'settings.lang_saved': 'Idioma actualizado.',
    'settings.address_title': 'Cartera',
    'settings.address_desc': 'Tu dirección de depósito en Polygon.',
    'settings.user_id_label': 'User ID',
    'settings.session_title': 'Sesión',
    'settings.signed_in_as': 'Sesión iniciada como',
    'settings.about_title': 'Acerca de',
    'settings.version_label': 'Versión',
    'settings.reset_lang_btn': 'Restablecer idioma',
  },
  zh: {
    'common.loading': '加载中…',
    'common.back_to_chat': '返回聊天',
    'common.copy_address': '复制地址',
    'common.copy_full_address': '复制完整地址',
    'common.copied': '✓ 已复制',
    'common.auth_error': '认证错误：{error}',
    'common.error': '错误：{message}',
    'common.balance': '余额',

    'home.title': 'Torian',
    'home.ready': 'Mini App 已就绪。',
    'home.loading_title': '正在准备 Mini App',
    'home.loading_desc': '正在验证你的 Telegram 会话并同步 Polymarket 钱包。',
    'home.loading_short': '正在验证会话。',
    'home.auth_eyebrow': '认证',
    'home.auth_failed': '认证失败',
    'home.wallet': '钱包：{address}',
    'home.foreign.title': '错误代币的资金',
    'home.foreign.body':
      '我们检测到您钱包中存在 Polymarket 不接受交易的代币。只有 <b>pUSD</b>（或通过 Bridge 的 <b>USDC</b>/<b>USDC.e</b>）才会显示为余额。',
    'home.foreign.list_label': '已检测到：',
    'home.foreign.cta': '您可以自己免 gas 取回这些资金 — 无需联系客服。',
    'home.foreign.cta_pusd': '转为 pUSD',
    'home.foreign.cta_eoa': '发送到我的钱包',
    'home.balance_title': 'pUSD 余额',
    'home.balance_loading': '加载中…',
    'home.deploy_wallet_cta': '部署钱包（免 gas）',
    'home.section_browse': '浏览',
    'home.section_social': '社交',
    'home.section_money': '资金',
    'home.tile.markets_title': '市场',
    'home.tile.markets_desc': '浏览 Polymarket 分类。',
    'home.tile.portfolio_title': '投资组合',
    'home.tile.portfolio_desc': '您的未平仓位。',
    'home.tile.orders_title': '订单',
    'home.tile.orders_desc': '订单簿上的限价单。',
    'home.tile.ranking_title': '排行榜',
    'home.tile.ranking_desc': '按成交量排名。',
    'home.tile.copy_title': '跟单',
    'home.tile.copy_desc': '跟随领导者并接收提醒。',
    'home.tile.deposit_title': '存入',
    'home.tile.deposit_desc': '充值 pUSD 余额。',
    'home.tile.withdraw_title': '提现',
    'home.tile.withdraw_desc': '从 Polymarket 转出 pUSD。',
    'home.tile.swap_title': '转换',
    'home.tile.swap_desc': 'USDC → pUSD（免 gas）。',
    'home.tile.bridge_title': 'Bridge',
    'home.tile.bridge_desc': '跨链存入。',
    'home.tile.approvals_title': '启用交易',
    'home.tile.approvals_desc': '一次性链上设置。',

    'rescue.preparing_title': '准备取回',
    'rescue.preparing_desc': '在扫描钱包前验证您的 Telegram 会话。',
    'rescue.preparing_short': '认证中…',
    'rescue.scanning_title': '正在扫描您的钱包',
    'rescue.scanning_desc': '读取链上余额并构建免 gas 的取回批。',
    'rescue.scanning_short': '扫描中…',
    'rescue.failed': '取回失败',
    'rescue.detected_label': '已检测',
    'rescue.detected_title': '待取回代币',
    'rescue.dest_label': '目的地',
    'rescue.gas_label': 'Gas',
    'rescue.signing': '签名中…',
    'rescue.submitting': '提交中…',
    'rescue.submitted_title': '取回已提交',
    'rescue.submitted_badge': '已提交',
    'rescue.submitted_body': '您的取回已发送到 Polymarket relayer',
    'rescue.submitted_desc':
      '确认通常在 ~30 秒内到达。余额会自动刷新。',
    'rescue.state_label': '状态',
    'rescue.view_on_polygonscan': '在 Polygonscan 查看',
    'rescue.eoa.title': '发送到我的钱包',
    'rescue.eoa.desc': '从 deposit wallet 将 POL / WMATIC / USDT 送回您自己的 EOA。免 gas，通过 Polymarket relayer。',
    'rescue.eoa.cta': '签名并发送到钱包',
    'rescue.pusd.title': '转为 pUSD',
    'rescue.pusd.desc':
      '包装原生 POL 并通过 Uniswap V3 在一个免 gas 批中全部转换为 pUSD。结果作为可交易的 pUSD 进入您的 deposit wallet。',
    'rescue.pusd.cta': '签名并转为 pUSD',
    'rescue.pusd.dest_value': 'pUSD （您的 deposit wallet）',

    'deploy.title': '钱包设置',
    'deploy.creating': '正在创建您的钱包…',
    'deploy.init_failed': '无法初始化钱包。',
    'deploy.ready_blurb':
      '您的钱包已就绪。Polymarket 在 Polygon 上使用智能钱包（代理）— 代理会在您首次充值时自动部署。',
    'deploy.your_address': '您的地址（EOA）',
    'deploy.backed_by': '由 {backend} 支持',
    'deploy.backend_web3auth': 'Web3Auth MPC',
    'deploy.backend_local': '本地密钥（开发模式）',
    'deploy.done': '完成 — 返回聊天',
    'deploy.close_hint': '您可以使用顶部的 ✕ 关闭此窗口。',

    'deposit.title': '充值',
    'deposit.blurb_bridge':
      '将 <b>Polygon</b> 上的 <b>USDC</b> 或 <b>USDC.e</b> 发送到下方的桥接地址。将自动转换为 pUSD 并入账。最低 $2。',
    'deposit.blurb_direct':
      '将 <b>Polygon</b> 上的 <b>pUSD</b> 发送到下方地址。',
    'deposit.bridge_label': 'Polymarket 桥（Polygon）',
    'deposit.direct_label': '您的充值地址',
    'deposit.not_deployed_title': '⚠ 钱包尚未部署。',
    'deposit.not_deployed_body':
      '合约在首次使用时部署。您可以先充值后部署，但交易需要先部署。',
    'deposit.deploy_btn': '立即部署（无 Gas）',
    'deposit.deploying': '部署中…',
    'deposit.footer_bridge':
      '仅限 Polygon。支持 USDC、USDC.e、USDT、DAI、WETH、POL、SAND。其他链通过 Polymarket Bridge。',
    'deposit.footer_direct':
      '仅在 Polygon 上发送 pUSD。直接发送其他代币可能造成永久损失。',
    'deposit.intent_buy_title': '完成充值以继续交易',
    'deposit.intent_buy_body':
      '你想用 <b>${usd}</b> 买入 <b>{outcome}</b>。钱包需要至少 <b>${required}</b>。',
    'deposit.intent_progress': '余额：<b>${balance}</b> / ${required}',
    'deposit.intent_short': '还差 <b>${missing}</b>',
    'deposit.intent_ready_title': '✅ 可以交易了',
    'deposit.intent_ready_body':
      '余额充足。返回聊天并再次点击同一按钮重试买入。',
    'deposit.intent_back_to_chat': '返回聊天',
    'deposit.intent_continue_button': '继续购买',
    'deposit.intent_continuing': '正在准备订单…',
    'deposit.loading_title': '正在准备存款',
    'deposit.loading_desc': '正在验证您的 Telegram 会话并解析跨链桥地址。',
    'deposit.loading_short': '正在解析跨链桥地址。',
    'deposit.auth_failed': '认证失败',
    'deposit.load_failed': '无法加载您的钱包',
    'deposit.qr_alt': '存款二维码',
    'deposit.qr_card_title': '扫描或复制以发送资金',
    'deposit.qr_card_desc': '二维码仅包含目标地址 — 请在发送应用中设置金额。',
    'deposit.field_network': '网络',
    'deposit.field_token': '代币',
    'deposit.field_status': '状态',
    'deposit.status_deployed': '已部署',
    'deposit.status_not_deployed': '未部署',
    'deposit.action_required': '需要操作',

    'withdraw.title': '提取 pUSD',
    'withdraw.title_sent': '提取已发送',
    'withdraw.title_confirm': '确认提取',
    'withdraw.authenticating': '认证中…',
    'withdraw.loading_balance': '加载余额…',
    'withdraw.sent_blurb':
      '已发送 <b>${amount}</b> pUSD 到 {to}。通常几秒内到账。',
    'withdraw.state': '状态',
    'withdraw.view_polygonscan': '在 Polygonscan 上查看',
    'withdraw.available': '可用',
    'withdraw.daily_limit': '每日限额：${limit}',
    'withdraw.daily_limit_label': '每日限额',
    'withdraw.remaining_today': '今日剩余：${remaining}',
    'withdraw.destination_label': '目标（Polygon 地址）',
    'withdraw.destination_warning':
      '仅 Polygon (MATIC) 网络。发送到错误网络 = 资金损失。',
    'withdraw.amount_label': '金额（pUSD）',
    'withdraw.max': '最大',
    'withdraw.exceeds_balance': '超出余额（${amount}）',
    'withdraw.exceeds_daily': '超出每日剩余（${amount}）',
    'withdraw.enter_positive': '请输入正数金额',
    'withdraw.preparing': '准备中…',
    'withdraw.review': '审核',
    'withdraw.to': '至',
    'withdraw.amount': '金额',
    'withdraw.network_fee': '网络费',
    'withdraw.gasless': '无 Gas（赞助）',
    'withdraw.remaining_after': '之后剩余',
    'withdraw.sign_send': '签名并发送',
    'withdraw.signing': '签名中…',
    'withdraw.submitting': '发送中…',
    'withdraw.back': '返回',

    'swap.eyebrow': 'Swap',
    'swap.title': '兑换 USDC → pUSD',
    'swap.description':
      'Polymarket 以 pUSD 结算。将钱包中的 USDC 兑换为 pUSD — 免 gas，通过 Polymarket 中继。',
    'swap.preparing_title': '准备兑换',
    'swap.preparing_desc':
      '在报价 USDC → pUSD 之前认证您的 Telegram 会话。',
    'swap.preparing_short': '认证会话。',
    'swap.quoting_title': '正在报价 USDC → pUSD',
    'swap.quoting_desc': '向 Polymarket 中继请求最新兑换汇率。',
    'swap.quoting_short': '正在报价。',
    'swap.step_session': '会话',
    'swap.step_wallet': '钱包',
    'swap.step_relayer': '中继',
    'swap.quote_label': '报价',
    'swap.from_label': '从',
    'swap.to_min_label': '至（最低）',
    'swap.wallet_label': '钱包',
    'swap.gas_label': 'Gas',
    'swap.gasless': '免 Gas',
    'swap.usdc_on_polygon': 'Polygon 上的 USDC',
    'swap.cta_sign': '签名并兑换',
    'swap.signing': '签名中…',
    'swap.submitting': '发送中…',
    'swap.failed_title': '兑换失败',
    'swap.auth_failed_title': '认证失败',
    'swap.no_balance_title': '没有可兑换的 USDC',
    'swap.no_balance_body':
      '您的钱包没有 USDC 或 USDC.e 余额可兑换。请先充值 USDC 后重试。',
    'swap.cta_deposit': '充值 USDC',
    'swap.submitted_eyebrow': '已提交',
    'swap.submitted_title': '兑换已提交',
    'swap.submitted_body': '您的兑换已进入 Polymarket 中继',
    'swap.submitted_desc':
      '确认通常在 ~30 秒内到达。您的 pUSD 余额会自动刷新。',
    'swap.state_label': '状态',
    'swap.view_polygonscan': '在 Polygonscan 查看',

    'market.eyebrow': '市场',
    'market.title': '浏览市场',
    'market.description': '选择一个分类查看 Polymarket 实时问题，或按名称搜索。',
    'market.breadcrumb': '市场',
    'market.search_label': '搜索',
    'market.search_placeholder': '例如：特朗普、世界杯、比特币…',
    'market.search_cta': '搜索',
    'market.categories_title': '分类',
    'market.categories_description': '十六个精选主题，来自 Polymarket 实时更新。',
    'market.cat.breaking': '热门',
    'market.cat.politics': '政治',
    'market.cat.trump': '特朗普',
    'market.cat.crypto': '加密',
    'market.cat.sports': '体育',
    'market.cat.elections': '选举',
    'market.cat.world': '世界',
    'market.cat.business': '商业',
    'market.cat.geopolitics': '地缘政治',
    'market.cat.finance': '金融',
    'market.cat.tech': '科技',
    'market.cat.culture': '文化',
    'market.cat.economy': '经济',
    'market.cat.mentions': '提及',
    'market.cat.climate': '气候',
    'market.cat.bonds': '债券',

    'market.list.eyebrow_cat': '{category}',
    'market.list.eyebrow_search': '搜索',
    'market.list.title_cat': '{category}',
    'market.list.title_search': '“{term}”',
    'market.list.description_cat': '此分类中的实时市场，来自 Polymarket 的最新数据。',
    'market.list.description_search': '在所有分类中与您的搜索匹配的市场。',
    'market.list.breadcrumb_cat': '{category}',
    'market.list.breadcrumb_search': '搜索：{term}',
    'market.list.loading_title': '正在加载市场…',
    'market.list.loading_desc': '正在从 Polymarket 获取最新问题和价格。',
    'market.list.loading_short': '加载市场。',
    'market.list.error_title': '无法加载市场',
    'market.list.empty_title': '未找到市场',
    'market.list.empty_cat': '此分类下现在没有实时市场。请尝试其他分类或稍后再查看。',
    'market.list.empty_search': '没有市场匹配 “{term}”。请尝试其他关键字或按分类浏览。',
    'market.list.cta_categories': '浏览分类',
    'market.list.volume24h': '24小时成交',
    'market.list.no_volume': '—',
    'market.list.ends': '结束 {date}',
    'market.list.prev': '上一页',
    'market.list.next': '下一页',
    'market.list.page_of': '第 {page} / {total} 页',

    'market.detail.eyebrow': '市场',
    'market.detail.breadcrumb': '市场',
    'market.detail.loading_title': '正在加载市场…',
    'market.detail.loading_desc': '正在从 Polymarket 获取价格、结果和订单簿预览。',
    'market.detail.loading_short': '加载市场。',
    'market.detail.error_title': '无法加载市场',
    'market.detail.outcomes_title': '结果',
    'market.detail.outcomes_description': '点击金额创建买入订单。您将在下一个页面上检查并签名。',
    'market.detail.preview_title': '订单簿预览',
    'market.detail.preview_description': '来自 CLOB 的最佳买/卖快照。实际成交取决于实时流动性。',
    'market.detail.bestbid_label': '最佳买入',
    'market.detail.bestask_label': '最佳卖出',
    'market.detail.spread_label': '价差',
    'market.detail.volume24h_label': '24小时成交量',
    'market.detail.volume_total_label': '总成交量',
    'market.detail.liquidity_label': '流动性',
    'market.detail.ends_label': '结束',
    'market.detail.about_title': '关于此市场',
    'market.detail.buy_for': '买入 {outcome} · ${amount}',
    'market.detail.drafting': '创建中…',
    'market.detail.min_order': '最低订单：${amount}',
    'market.detail.auth_required': '请认证您的 Telegram 会话以下单。',
    'market.detail.draft_failed_title': '无法创建订单',
    'market.detail.no_balance_title': 'pUSD 余额不足',
    'market.detail.no_balance_body': '您有 ${have}，该订单需要 ${need}。请充值后重试。',
    'market.detail.deposit_cta': '充值 pUSD',
    'market.detail.approvals_title': '请先启用交易',
    'market.detail.approvals_body':
      '在下第一笔订单之前，您需要进行一次性设置，授权 Polymarket 从您的存款钱包转移资金。无需 gas，只需设置一次。',
    'market.detail.approvals_cta': '启用交易',

    'portfolio.eyebrow': '投资组合',
    'portfolio.breadcrumb': '投资组合',
    'portfolio.title': '您的投资组合',
    'portfolio.description': 'Polymarket 上的持仓。每股在您边获胜时支付 $1。',
    'portfolio.loading_title': '正在加载您的投资组合…',
    'portfolio.loading_desc': '正在从 Polymarket 获取持仓和当前价值。',
    'portfolio.loading_short': '加载持仓。',
    'portfolio.error_title': '无法加载投资组合',
    'portfolio.empty_title': '您还没有持仓',
    'portfolio.empty_intro': '分 3 步进行您的首次预测：',
    'portfolio.empty_step_1': '1. 充值 pUSD — 为您的钱包充值（USDC、USDT 或 pUSD）。',
    'portfolio.empty_step_2': '2. 浏览市场 — 选择您感兴趣的问题。',
    'portfolio.empty_step_3': '3. 买入 YES 或 NO — 每股在您边获胜时支付 $1。',
    'portfolio.empty_cta_deposit': '充值 pUSD',
    'portfolio.empty_cta_markets': '浏览市场',
    'portfolio.empty_cta_help': '运作原理',
    'portfolio.totals_title': '总计',
    'portfolio.payout_label': '最大支付',
    'portfolio.value_label': '当前价值',
    'portfolio.size_label': '股数',
    'portfolio.avg_label': '均价',
    'portfolio.cur_label': '现价',
    'portfolio.pnl_label': '损益',
    'portfolio.resolves_label': '解决于',
    'portfolio.redeemable_badge': '可赎回',
    'portfolio.cta_open_market': '查看市场',

    'orders.eyebrow': '订单',
    'orders.breadcrumb': '订单',
    'orders.title': '未成交订单',
    'orders.description': 'Polymarket 订单簿中的限价单。点击卡片查看市场或取消。',
    'orders.loading_title': '正在加载订单…',
    'orders.loading_desc': '正在从 Polymarket CLOB 获取未成交订单。',
    'orders.loading_short': '加载订单中。',
    'orders.error_title': '无法加载订单',
    'orders.empty_title': '没有{status}订单',
    'orders.empty_intro': '在任意市场创建限价单以预定价格。未成交订单会显示在这里，直到成交或被取消。',
    'orders.empty_cta_markets': '浏览市场',
    'orders.filter_all': '全部',
    'orders.filter_open': '未成交',
    'orders.filter_pending': '处理中',
    'orders.filter_matched': '已成交',
    'orders.filter_cancelled': '已取消',
    'orders.filter_failed': '失败',
    'orders.status_pending': '处理中',
    'orders.status_open': '未成交',
    'orders.status_matched': '已成交',
    'orders.status_cancelled': '已取消',
    'orders.status_failed': '失败',
    'orders.side_buy': '买入',
    'orders.side_sell': '卖出',
    'orders.size_label': '数量',
    'orders.price_label': '价格',
    'orders.total_label': '总额',
    'orders.created_label': '提交于',
    'orders.type_label': '类型',
    'orders.cancel_btn': '取消订单',
    'orders.cancelling': '取消中…',
    'orders.cancel_confirm': '确定取消此订单？此操作无法撤销。',
    'orders.cancel_failed_title': '无法取消订单',
    'orders.view_market': '查看市场',

    'ranking.eyebrow': '排行榜',
    'ranking.title': '顶尖交易者',
    'ranking.description': '按交易量排名的用户。',
    'ranking.breadcrumb': '排行榜',
    'ranking.loading_title': '加载排行榜',
    'ranking.loading_desc': '正在汇总所选时间段的成交量。',
    'ranking.loading_short': '加载中…',
    'ranking.empty_title': '暂无交易者',
    'ranking.empty_desc': '此时间段内没有交易记录。',
    'ranking.error_title': '无法加载排行榜',
    'ranking.period_day': '24小时',
    'ranking.period_week': '本周',
    'ranking.period_month': '本月',
    'ranking.period_all': '全部',
    'ranking.volume_label': '成交量',
    'ranking.trades_label': '交易数',
    'ranking.you_badge': '你',
    'ranking.anonymous': '匿名',

    'copy.eyebrow': '跟单',
    'copy.title': '正在跟随',
    'copy.description': '当这些领导者交易时接收通知。自动交易即将上线。',
    'copy.breadcrumb': '跟单',
    'copy.loading_title': '加载跟随列表',
    'copy.loading_desc': '获取您跟随的领导者。',
    'copy.loading_short': '加载中…',
    'copy.empty_title': '您还未跟随任何人',
    'copy.empty_desc': '添加领导者地址以在他们交易时获得提醒。',
    'copy.add_title': '跟随领导者',
    'copy.add_address_label': '领导者地址',
    'copy.add_address_placeholder': '0x…',
    'copy.add_max_label': '每笔最大 USD（可选）',
    'copy.add_mult_label': '数量倍数（可选）',
    'copy.add_btn': '跟随领导者',
    'copy.adding': '跟随中…',
    'copy.add_error_title': '无法跟随',
    'copy.invalid_address': '请输入有效的 0x 地址（40 位 16 进制）。',
    'copy.unfollow_btn': '取消跟随',
    'copy.unfollow_confirm': '取消跟随该领导者？',
    'copy.unfollowing': '移除中…',
    'copy.unfollow_error_title': '无法取消跟随',
    'copy.row_max': '最大 ${max}',
    'copy.row_mult': '{mult}×',
    'copy.row_since': '跟随起于 {date}',
    'copy.active': '活跃',
    'copy.inactive': '未激活',
    'copy.notice_title': '仅通知',
    'copy.notice_desc': '领导者交易时您会收到提醒。自动交易需要会话密钥。',
    'copy.top_title': '顶级交易者',
    'copy.top_loading': '正在加载顶级交易者…',
    'copy.top_empty': '暂无交易者，请稍后再来。',
    'copy.top_volume': '成交量 {vol}',
    'copy.top_pnl': '盈亏 {pnl}',
    'copy.leader.breadcrumb': '交易者',
    'copy.leader.eyebrow': '交易者',
    'copy.leader.loading_title': '正在加载交易者',
    'copy.leader.loading_desc': '正在获取该交易者的近期活动。',
    'copy.leader.loading_short': '加载中…',
    'copy.leader.title': '交易者资料',
    'copy.leader.load_failed': '无法加载交易者',
    'copy.leader.not_found': '未找到该交易者。',
    'copy.leader.how_title': '跟单如何运作',
    'copy.leader.how_desc': '我们会通过 Telegram 通知您该交易者的最新动作。每条提醒都带有按钮，可打开应用一键复制该订单。',
    'copy.leader.follow_title': '关注并接收提醒',
    'copy.leader.follow_btn': '关注交易者',
    'copy.leader.recent_title': '近期交易',
    'copy.leader.recent_empty': '暂无近期交易。',
    'copy.leader.just_now': '刚刚',
    'copy.leader.mins_ago': '{n}分钟前',
    'copy.leader.hours_ago': '{n}小时前',
    'copy.leader.days_ago': '{n}天前',
    'copy.confirm.breadcrumb': '复制',
    'copy.confirm.eyebrow': '一键跟单',
    'copy.confirm.loading_title': '正在加载信号',
    'copy.confirm.loading_desc': '正在获取要复制的交易。',
    'copy.confirm.loading_short': '加载中…',
    'copy.confirm.title': '复制该交易',
    'copy.confirm.description': '复制 {leader} 的操作。',
    'copy.confirm.load_failed': '无法加载信号',
    'copy.confirm.not_found': '未找到信号或已过期。',
    'copy.confirm.side_buy': '买入',
    'copy.confirm.side_sell': '卖出',
    'copy.confirm.field_outcome': '结果',
    'copy.confirm.field_price': '价格',
    'copy.confirm.field_leader': '交易者',
    'copy.confirm.field_status': '状态',
    'copy.confirm.amount_title': '金额',
    'copy.confirm.amount_label': '投入的 USD',
    'copy.confirm.action_error': '操作失败',
    'copy.confirm.handled_title': '已处理',
    'copy.confirm.handled_desc': '该信号已执行或已忽略。',
    'copy.confirm.confirming': '准备中…',
    'copy.confirm.confirm_btn': '立即复制',
    'copy.confirm.dismiss_btn': '忽略',

    'help.eyebrow': '帮助',
    'help.title': '如何使用 Torian',
    'help.description': 'Mini App 与机器人命令的快速参考。',
    'help.breadcrumb': '帮助',
    'help.section_app': 'Mini App',
    'help.section_bot': '机器人命令',
    'help.section_links': '资源',
    'help.app.markets_label': '市场',
    'help.app.markets_desc': '浏览分类并搜索 Polymarket 市场。',
    'help.app.portfolio_label': '投资组合',
    'help.app.portfolio_desc': '您的未平仓位与总价值。',
    'help.app.orders_label': '订单',
    'help.app.orders_desc': '您的活动与历史限价单。',
    'help.app.deposit_label': '充值 / Bridge',
    'help.app.deposit_desc': '通过 Polygon USDC 或 Polymarket Bridge 充值 pUSD。',
    'help.app.withdraw_label': '提现',
    'help.app.withdraw_desc': '将 pUSD 取回到您在 Polygon 上的钱包。',
    'help.app.ranking_label': '排行榜',
    'help.app.ranking_desc': '按交易量排名的顶尖交易者。',
    'help.app.copy_label': '跟单',
    'help.app.copy_desc': '跟随领导者并获得交易提醒。',
    'help.bot.market': '`/market <词>` — 搜索市场',
    'help.bot.portfolio': '`/portfolio` — 查看投资组合',
    'help.bot.wallet': '`/wallet` — 查看钱包',
    'help.bot.deposit': '`/deposit` — 充值',
    'help.bot.withdraw': '`/withdraw` — 提现',
    'help.bot.whale': '`/whale` — 近期巨鲸交易',
    'help.bot.copy': '`/copy` — 跟随领导者（仅提醒）',
    'help.bot.ranking': '`/ranking` — 顶尖交易者',
    'help.bot.lang': '`/lang` — 切换语言',
    'help.bot.help': '`/help` — 显示此消息',
    'help.links.polymarket': '打开 Polymarket',
    'help.links.support': '联系支持',

    'settings.eyebrow': '设置',
    'settings.title': '设置',
    'settings.description': '管理 Mini App 偏好。',
    'settings.breadcrumb': '设置',
    'deposit.breadcrumb': '充值',
    'withdraw.breadcrumb': '提取',
    'swap.breadcrumb': '兑换',
    'deploy.breadcrumb': '部署',
    'rescue.breadcrumb': '救援',
    'approvals.breadcrumb': '授权',
    'bridge.breadcrumb': '桥接',
    'sign_order.breadcrumb': '签署订单',
    'settings.lang_title': '语言',
    'settings.lang_desc': '仅用于 Mini App。机器人保留自身设置。',
    'settings.lang_auto': '自动（来自 Telegram）',
    'settings.lang_saved': '语言已更新。',
    'settings.address_title': '钱包',
    'settings.address_desc': '您在 Polygon 上的存入地址。',
    'settings.user_id_label': '用户 ID',
    'settings.session_title': '会话',
    'settings.signed_in_as': '已登录为',
    'settings.about_title': '关于',
    'settings.version_label': '版本',
    'settings.reset_lang_btn': '重置语言',
  },
};

export function detectLang(): Lang {
  const stored = getStoredLang();
  if (stored) return stored;
  const code = tryGetTelegram()?.initDataUnsafe?.user?.language_code?.toLowerCase() ?? '';
  if (code.startsWith('pt')) return 'pt-BR';
  if (code.startsWith('es')) return 'es';
  if (code.startsWith('zh')) return 'zh';
  return 'en';
}

export type TFunction = (key: string, params?: Record<string, string | number>) => string;

export function makeT(lang: Lang): TFunction {
  return (key, params) => {
    const raw = DICT[lang]?.[key] ?? DICT.en[key] ?? key;
    if (!params) return raw;
    return raw.replace(/\{(\w+)\}/g, (_, k) =>
      params[k] !== undefined ? String(params[k]) : `{${k}}`,
    );
  };
}

/** React hook that returns a memoized translator for the active language. */
export function useT(): TFunction {
  const lang = useLang();
  return useMemo(() => makeT(lang), [lang]);
}

/** React hook that returns the currently active language and re-renders on change. */
export function useLang(): Lang {
  return useSyncExternalStore(
    subscribeLang,
    () => detectLang(),
    () => 'en' as Lang,
  );
}
